import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon } from "lucide-react";
import { FlagUS, FlagMX } from "@/components/ui/LanguageFlagToggle";
import type { TFunction } from "i18next";
import { useAuth } from "@/contexts/useAuth";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { DIRECTOR_ROUTE_PREFIX, getDirectorCatalog, pollChatStatus, sendChatMessage, submitDirectorFeedback, type DirectorPlan } from "@/lib/media-agent-chat";
import { useStoryAvailability } from "@/lib/story-availability";
import { api } from "@/lib/api";
import { buildOffScriptReply } from "@/lib/director-offscript";
import { resolveBackendBase } from "@/lib/resolve-backend-base";
import { uploadToWebStorage } from "@/lib/web-upload";
import UserMediaGalleryDialog, { type UserMediaGalleryItem } from "@/components/media/UserMediaGalleryDialog";
import CorridoChatFlow from "@/components/landing/CorridoChatFlow";
import StoryChatFlow from "@/components/landing/StoryChatFlow";
import AdsChatFlow from "@/components/landing/AdsChatFlow";
import StoryboardCanvas from "@/components/landing/director-studio/StoryboardCanvas";
import { useClipReview, useStoryboardProject, useStoryBrief, useStudioProgress, useWorkflowStartEvent, type StudioService, type WorkflowId } from "@/components/landing/director-studio/studioBus";
import type { ConsoleToolId } from "@/components/landing/ToolCompass";
import { trackEvent } from "@/lib/analytics";
import { useUserCredits, refreshUserCredits } from "@/hooks/useUserCredits";

const CONVERSATION_STORAGE_KEY = "media_agent_conversation_id";
const USER_STORAGE_KEY = "media_agent_user_id";
export const LANDING_DIRECTOR_DRAFT_STORAGE_KEY = "landing_director_draft_v1";
const DRAFT_STORAGE_KEY = LANDING_DIRECTOR_DRAFT_STORAGE_KEY;
// Chat/canvas seam: remembered width, and the floors that stop either side
// from being dragged out of existence.
const CHAT_WIDTH_KEY = "director-console:chat-width";
const CHAT_MIN = 320;
const CANVAS_MIN = 360;

// In-flight render survives a page refresh. When a job goes to "running" we
// stash its run id here; on reload the console reconnects the poll and re-shows
// the progress bar. Without this, refreshing (or a tab reload) during a render
// orphaned the job — the GPU kept working but the UI reset to the greeting with
// no way back and no signal the render was still alive. The TTL guards against
// a stale id left behind by a run that finished while the tab was closed.
const ACTIVE_RUN_STORAGE_KEY = "director-console:active-run-v1";
const ACTIVE_RUN_TTL_MS = 2 * 60 * 60 * 1000; // 2h — longer than any real render

type ActiveRunRecord = { runId: string; subTypeId: string; launchedAt: number };

function saveActiveRun(runId: string | undefined, subTypeId: string | undefined) {
  if (typeof window === "undefined" || !runId) return;
  try {
    window.localStorage.setItem(
      ACTIVE_RUN_STORAGE_KEY,
      JSON.stringify({ runId, subTypeId: subTypeId ?? "", launchedAt: Date.now() } satisfies ActiveRunRecord),
    );
  } catch { /* quota — losing resume is better than throwing mid-launch */ }
}

function clearActiveRun() {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(ACTIVE_RUN_STORAGE_KEY); } catch { /* quota */ }
}

function readActiveRun(): ActiveRunRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ACTIVE_RUN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveRunRecord>;
    if (!parsed || typeof parsed.runId !== "string" || !parsed.runId.trim()) return null;
    if (typeof parsed.launchedAt !== "number" || Date.now() - parsed.launchedAt > ACTIVE_RUN_TTL_MS) {
      clearActiveRun();
      return null;
    }
    return { runId: parsed.runId, subTypeId: typeof parsed.subTypeId === "string" ? parsed.subTypeId : "", launchedAt: parsed.launchedAt };
  } catch {
    return null;
  }
}

if (typeof document !== "undefined" && !document.getElementById("landing-director-console-styles")) {
  const style = document.createElement("style");
  style.id = "landing-director-console-styles";
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&display=swap');
    @keyframes ldc-blink{0%,80%,100%{transform:scale(.6);opacity:.2}40%{transform:scale(1);opacity:1}}
    @keyframes ldc-fadein{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    @keyframes ldc-shimmer{0%,100%{opacity:.5}50%{opacity:1}}
    /* The workspace: storyboard canvas + chat, side by side. The canvas is not
       a panel you open — it is the work surface, always on screen. Below 900px
       there isn't room for two columns, so they stack with the canvas on top
       (still visible, never behind a toggle). */
    .ldc-workspace{display:flex;flex:1;min-height:0;position:relative;z-index:5}
    /* IN-PAGE HEIGHT (2026-09-25): svh, not dvh. The console sits in the
       page flow, and a dvh height grew ~80px the moment Safari's toolbar
       collapsed on the first scroll — with the console on screen — which
       shoved everything below it down and reflowed the chat mid-read. svh is
       the toolbar-showing height, so it never moves (the hero does the same).
       The vh lines are the fallback for iOS < 15.4. The 640px floor only
       applies where there is room (a landscape phone is shorter than that).
       The maximized overlay keeps its inline 100dvh: fixed to the viewport,
       it should follow the toolbar. */
    .ldc-root{height:min(calc(100vh - 40px),1080px);height:min(calc(100svh - 40px),1080px);min-height:min(640px,82vh);min-height:min(640px,82svh)}
    /* An EMPTY board has nothing to pan or zoom, but React Flow's pane still
       took every wheel turn and touch drag over it, so the page stopped
       scrolling wherever a thumb or cursor rested on the board (2026-09-25).
       The empty-state card and the toolbar are siblings of .react-flow, so
       they stay interactive; a real board pans and zooms as before. */
    .ldc-canvas [data-testid="studio-canvas"][data-empty] .react-flow{pointer-events:none}
    /* Canvas + chat are NEUTRAL GRAPHITE — no hue. The saturated teal/green +
       plum/aubergine (field feedback 2026-07-22: "some of the most hideous
       colours... they throw everything off") are gone. Both panels are a clean
       grey with light text, kept distinct by a small tonal step (board a touch
       lighter than chat) plus the cyan seam and a section label on each — so
       nothing reads coloured, and neither melts into the near-black page
       (#0a0b0f) or into each other. */
    .ldc-canvas{flex:1;min-width:0;position:relative;background:linear-gradient(160deg,#20242c,#171a20)}
    .ldc-chat{flex-shrink:0;display:flex;flex-direction:column;min-height:0;position:relative;border-left:3px solid #38bdf8;box-shadow:-1px 0 0 0 rgba(56,189,248,.4);background:linear-gradient(160deg,#171a20,#111318)}
    /* A small always-on label so the two regions are unmistakable. */
    .ldc-canvas::before{content:var(--ldc-board-label,"");position:absolute;top:8px;right:12px;z-index:6;font:700 11px/1 'Rajdhani',sans-serif;letter-spacing:2px;color:#9aa4b2;pointer-events:none}
    .ldc-chat-label{font:700 11px/1 'Rajdhani',sans-serif;letter-spacing:2px;color:#9aa4b2;padding:8px 12px 0}
    /* CapCut .resizeHandle: 8px, ew-resize, pinned to the panel's left edge. */
    .ldc-resize{position:absolute;left:-5px;top:0;bottom:0;width:10px;cursor:ew-resize;z-index:10;background:transparent;transition:background .15s}
    .ldc-resize:hover,.ldc-resize[data-dragging="true"]{background:rgba(56,189,248,.5)}
    @media (max-width:900px){
      .ldc-workspace{flex-direction:column}
      .ldc-canvas{flex:none;height:40%;min-height:200px;border-bottom:2px solid #38bdf8}
      /* Stacked: a horizontal drag would resize the wrong axis, so the handle
         goes away and the chat just takes the remaining height. */
      .ldc-chat{width:auto!important;min-width:0;flex:1;border-left:none}
      .ldc-resize{display:none}
    }
    /* LANDSCAPE PHONE (2026-09-25). Stacked, a 320-390px-tall console gave
       the board its 200px floor and left the chat 0-63px: the input and Send
       sat below the console's clipped bottom edge. A landscape phone is wide,
       so board and chat go back to side by side and each gets the full
       height. The drag seam stays hidden (the 900px rule); the split is
       fixed. Above 900px the desktop row layout already applies. */
    @media (max-height:500px) and (min-width:561px) and (max-width:900px){
      .ldc-workspace{flex-direction:row}
      .ldc-canvas{flex:1 1 45%;height:auto;min-height:0;border-bottom:none}
      .ldc-chat{flex:1 1 55%;min-width:0;border-left:3px solid #38bdf8}
    }
    /* TOUCH (2026-09-24): on a tablet the header's icon buttons were 30px
       squares; a finger gets 40px. Phone widths refine this further down. */
    @media (pointer:coarse){
      .ldc-header-actions button,.ldc-header-actions a{min-height:40px;min-width:40px;justify-content:center}
    }
    /* A LANDSCAPE PHONE is a phone (2026-09-25): 740x360, 844x390, 915x412
       and 932x430 are all wider than the PHONE HEADER query below, so a thumb
       got the tablet's 40px buttons. Full 44px targets on any short touch
       screen; the labels stay (they fit at 740). */
    @media (pointer:coarse) and (max-height:500px){
      .ldc-header-actions button,.ldc-header-actions a,.ldc-header-actions [data-testid^="landing-director-lang-"]{min-height:44px;min-width:44px}
    }
    /* RAP (coming soon, 2026-09-25). With no service, the empty board fell
       through to the TikTok story's promise ("its plan lands on this board —
       the narrator, the tone…"), under a pitch and a chat that both say the
       tool is not open yet. The board keeps only its title there. */
    .ldc-canvas [data-testid="studio-canvas-empty"][data-service="none"] p{display:none}
    /* PHONE. At 900px there is still room to give the board 40% of a tall
       window; at 560px there is not — the board shrinks to a strip you cannot
       judge a storyboard in, and the chat loses the height it needs to show a
       card and the input at once. Give the board a fixed, legible slice and
       let the chat scroll. */
    @media (max-width:560px){
      .ldc-canvas{height:auto;flex:0 0 clamp(180px,32vh,260px);min-height:0;overflow:hidden}
      /* 11px, not 9 (2026-09-24): 9px labels were unreadable on a phone. */
      .ldc-chat-label,.ldc-canvas::before{font-size:11px;letter-spacing:1.5px}
      /* The board's own toolbar and the chat cards are the two things that
         overflow first on a narrow screen. */
      .ldc-canvas .react-flow__panel{max-width:100%}
    }
    /* EMPTY BOARD (2026-09-24). The desktop card (absolute, 24px in, no
       height cap) ran past the bottom of the board strip, over the chat and
       its Sign up button, with the zoom pill drawn across its text. The
       pan/zoom pill and the board label go while the board is empty: there
       is nothing to pan yet, and the card's own title names the board.
       max-height:500px as well as the phone width: a landscape phone
       (667-915 x 360-412) gets a short board too. The width cap keeps a
       readable line on the wider landscape board. 15px copy in DM Sans, like
       the rest of the page on a phone (was 13px).
       Where the card is taller than the board (landscape, and any phone
       browser without :has — see the PHONE rule below) it scrolls inside
       the board. 2026-09-25: the text itself fades out over the last 28px
       (a mask, not the old background "shadows", which painted UNDER the
       text and never hid a sliced line), and the bottom padding lets the
       last line clear the fade at the end. overscroll-behavior stays auto,
       so once the card is at its end the swipe moves the page again —
       contain trapped the page on the card. */
    @media (max-width:560px),(max-height:500px){
      .ldc-canvas [data-testid="studio-canvas-empty"]{
        top:10px!important;left:10px!important;right:10px;width:auto!important;max-width:min(640px,calc(100% - 20px))!important;
        max-height:calc(100% - 20px);box-sizing:border-box;padding:14px 16px 28px!important;
        overflow-y:auto;-webkit-overflow-scrolling:touch;background:#0a0d12!important;
        -webkit-mask-image:linear-gradient(#000 calc(100% - 28px),transparent);mask-image:linear-gradient(#000 calc(100% - 28px),transparent);
      }
      .ldc-canvas [data-testid="studio-canvas-empty"]>div:first-child{letter-spacing:1.5px!important}
      .ldc-canvas [data-testid="studio-canvas-empty"] p{font-size:15px!important;line-height:1.5!important;font-family:'DM Sans',system-ui,sans-serif}
      .ldc-canvas [data-testid="studio-canvas"][data-empty] [data-testid="studio-canvas-toolbar"]{display:none!important}
      .ldc-canvas:has([data-testid="studio-canvas"][data-empty])::before{display:none}
      /* CHAT TEXT: 13px system-ui read smaller than, and in a different face
         from, the 15px DM Sans everywhere else on a phone — and beside the
         15px board card above it. */
      .ldc-bubble{font-size:15px!important;line-height:1.55!important;font-family:'DM Sans',system-ui,sans-serif!important}
    }
    /* CHAT LOG TOP EDGE (2026-09-25). Scrolled to the latest message, the
       first visible line was cut through its letters under the DIRECTOR CHAT
       label. It fades out under the label instead; 14px of top padding keeps
       the first bubble's text clear of the fade when the log is at the top. */
    @media (max-width:560px){
      .ldc-chat-log{padding-top:14px!important;-webkit-mask-image:linear-gradient(to bottom,transparent 0,rgba(0,0,0,.4) 8px,#000 22px);mask-image:linear-gradient(to bottom,transparent 0,rgba(0,0,0,.4) 8px,#000 22px)}
    }
    /* SHORT PHONES (iPhone SE and friends, 2026-09-24): a 180px board left
       the chat about three lines. The chat is where the work happens here —
       a full storyboard opens in its own full-screen sheet — so the strip
       gives up the height. */
    @media (max-width:560px) and (max-height:700px){
      .ldc-canvas{flex-basis:clamp(150px,28vh,200px);flex-basis:clamp(150px,28svh,200px)}
    }
    /* EMPTY BOARD ON A PHONE (2026-09-25). In the 150-260px strip the card
       hid up to 60% of its copy — the pricing line included — in a nested
       scroll box iOS gives no scrollbar, and a swipe that started on it
       scrolled the box instead of the page. While the board is empty the
       card now sits in the flow at its full height: the board grows to fit
       it, the in-page console grows by the same amount (height:auto, same
       floor), and the page scroll carries the difference. Once a board
       exists the strip and the fixed console height come back unchanged.
       The maximized overlay (not .ldc-root) cannot grow, so it keeps the
       scrolling card. Browsers without :has keep the scrolling card too
       (the @supports gate). */
    @supports selector(:has(a)){
      @media (max-width:560px){
        .ldc-root:has([data-testid="studio-canvas"][data-empty]){height:auto}
        .ldc-root .ldc-canvas:has([data-testid="studio-canvas"][data-empty]){flex:0 0 auto;height:auto}
        .ldc-root .ldc-canvas [data-testid="studio-canvas"][data-empty]{height:auto!important}
        /* The dotted board stays behind the card, filling whatever height
           the card gives it. */
        .ldc-root .ldc-canvas [data-testid="studio-canvas"][data-empty]>.react-flow{position:absolute!important;inset:0}
        .ldc-root .ldc-canvas [data-testid="studio-canvas-empty"]{
          position:relative!important;top:auto!important;left:auto!important;right:auto;
          margin:10px;max-width:none!important;max-height:none;overflow:visible;padding-bottom:14px!important;
          -webkit-mask-image:none;mask-image:none;
        }
        /* THE CHAT BESIDE AN EMPTY BOARD (2026-09-25). It was a fixed
           ~340px log that opens scrolled to the bottom, so the Director's
           greeting started mid-sentence with its hook hidden above the
           log's top edge (48-211px of it; most in Spanish at 320). While the
           board is empty the chat is as tall as what it holds, like the card
           above it, and the page scroll carries the height. The cap keeps
           log + input on one screen under the navbar (even at 568px tall);
           past it the log scrolls (and auto-scrolls) as before. The log takes
           any spare height if the console's floor is ever taller than its
           content. */
        .ldc-root:has([data-testid="studio-canvas"][data-empty]) .ldc-chat{flex:1 0 auto;height:auto}
        .ldc-root:has([data-testid="studio-canvas"][data-empty]) .ldc-chat-log{flex:1 1 auto!important;max-height:min(600px,75vh);max-height:min(600px,75svh)}
        /* SHORT PHONES, EMPTY BOARD (2026-09-25). On 320x568 the 75svh cap
           (426px) was shorter than the greeting: the log ended mid-sentence
           and hid the Sign up / Log in button up to 106px (ES ads) inside a
           nested scroll box iOS shows no scrollbar for. There the cap goes:
           the page scroll carries the whole greeting and its button, as it
           does the board card, and the input follows right under it. Once a
           board exists :has stops matching and the capped, auto-scrolling
           log comes back. Must stay after the 75svh rule (same
           specificity); the rap rule below keeps its own cap. */
        @media (max-height:700px){
          .ldc-root:has([data-testid="studio-canvas"][data-empty]) .ldc-chat-log{max-height:none}
        }
        /* RAP (coming soon, 2026-09-25): the chat holds one short bubble, and
           the full-height log left ~200px of empty panel under it. There the
           console drops its floor and shrinks to what it holds, with a
           smaller cap. Same specificity as the rules above, so it must stay
           after them. */
        .ldc-root:has([data-testid="studio-canvas-empty"][data-service="none"]){min-height:0}
        .ldc-root:has([data-testid="studio-canvas-empty"][data-service="none"]) .ldc-chat{flex:0 0 auto}
        .ldc-root:has([data-testid="studio-canvas-empty"][data-service="none"]) .ldc-chat-log{flex:0 1 auto!important;max-height:min(420px,55vh);max-height:min(420px,55svh)}
      }
    }
    /* PHONE HEADER. Brand wordmark + language + credits + status + two icon
       buttons do not fit on one 345px row, so the brand block collapses to the
       avatar and the controls keep their tap targets. Small landscape phones
       too (2026-09-25): at 568-667 x 320-375 the labelled header wrapped to
       two rows and took ~30px of a chat that had ~70px. */
    @media (max-width:560px),(max-height:500px) and (max-width:700px){
      /* !important: the header's padding and gap are inline styles, so
         without it this rule never applied (the header kept 16px sides). */
      .ldc-header{padding:6px 10px!important;gap:8px!important}
      .ldc-brand>div{display:none}
      /* The controls sit beside the avatar (basis 0, so the header never
         drops them onto a row of their own), grouped on the left, with the
         status word last and pushed right. A status that cannot fit beside
         them ("SIN CONEXIÓN" on the rap stop) takes its own right-aligned
         line instead of splitting the buttons around it (2026-09-25: at
         412px the avatar sat alone on one row and the status in the middle
         of the next). */
      .ldc-header-actions{flex:1 1 0;min-width:0;justify-content:flex-start!important;gap:6px!important;row-gap:2px!important}
      .ldc-status{order:9;margin-left:auto}
      /* Icons carry the meaning; the words are what overflow. Each button keeps
         an aria-label (or a title the name falls back to), and the glyphs are
         aria-hidden, so a screen reader never hears just "⛶" or "↺"
         (2026-09-25). The Home and Expand names contain the word the desktop
         shows, so voice control can say what it sees. */
      .ldc-btn-label{display:none}
      /* Apple and Android both ask for ~44px; these were 26-30px (and the
         restart button 30px wide). 2026-09-25: full 44px squares 6px apart —
         they were 36-40 x 40, the two flags 4px apart, and a near-miss on
         one switched the Director's language. */
      .ldc-header-actions button,.ldc-header-actions a{min-height:44px;min-width:44px;justify-content:center}
      .ldc-header-actions [role="group"]{gap:6px!important}
      .ldc-header-actions [data-testid^="landing-director-lang-"]{min-height:44px;padding:6px 8px}
      /* The status word's 3px tracking is what pushed ↺ onto a row of its own
         on the rap stop ("OFFLINE" is the widest word). */
      .ldc-status span{letter-spacing:1.5px!important}
      /* The screening banner wrapped to two lines on its 2.5px tracking. */
      .ldc-screening{letter-spacing:.4px!important;padding:6px 12px!important;line-height:1.3}
    }
    /* CHAT INPUT ROW (2026-09-24). With the forced 16px iOS input font the
       input had no min-width:0 and Send kept an 80px floor, so at 320px the
       Send button ran off the console. Both shrink now, and a cut
       placeholder ends in an ellipsis instead of a sliced letter. Landscape
       phones too (2026-09-25): at 568px wide the chat column is ~310px, and
       Send's floor put it past the chat's right edge. */
    @media (max-width:560px),(max-height:500px){
      .ldc-chat-form{gap:8px!important;padding:8px 10px 10px!important}
      .ldc-chat-input{min-width:0;flex:1 1 0!important;min-height:44px;box-sizing:border-box}
      .ldc-chat-input,.ldc-chat-input::placeholder{text-overflow:ellipsis}
      .ldc-chat-send{min-width:0!important;padding:10px 14px!important;min-height:44px;flex-shrink:0}
    }
    /* NARROW PHONES (≤400px, 2026-09-24): the D avatar dropped onto a row of
       its own above the controls, which cost the chat ~40px of height. The
       chat bubbles already carry the same avatar, so the header keeps only
       its controls (laid out by the PHONE HEADER rules above). At 320 the
       status word takes its own line rather than the buttons shrinking
       below 44px again (2026-09-25). */
    @media (max-width:400px){
      .ldc-brand{display:none!important}
      /* The same avatar beside every Director bubble took ~42px of a 290px
         chat, so 15px copy wrapped every few words. The header avatar is
         already gone at this width; the bubbles keep their shape and
         left alignment to say who is speaking. */
      .ldc-bubble-avatar{display:none!important}
      .ldc-bubble[data-from="director"]{max-width:100%!important}
      .ldc-status span{letter-spacing:1px!important}
    }
    /* 320-CLASS PHONES (2026-09-25): five 44px buttons fill the row, so every
       status word ("READY" too) took a ~30px line of its own above the
       banner. The coloured dot stays beside ↺; the word stays in the DOM for
       screen readers. */
    @media (max-width:359px){
      .ldc-status span{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
    }
    /* Fullscreen on a phone has to clear the notch and the home indicator, or
       the header sits under the status bar and the chat input under the bar. */
    @supports (padding: env(safe-area-inset-top)){
      .ldc-maximized{
        padding-top:env(safe-area-inset-top);
        padding-bottom:env(safe-area-inset-bottom);
        padding-left:env(safe-area-inset-left);
        padding-right:env(safe-area-inset-right);
        box-sizing:border-box;
      }
    }
  `;
  document.head.appendChild(style);
}

const P = {
  bg: "#05070b",
  // The console's own panel colour. The landing page sits on --background
  // (220 18% 5% ≈ #0a0b0f), so P.bg made the console melt into the page —
  // this is deliberately lifted a few steps so the workspace reads as a
  // physical panel on top of the page.
  console: "#0b1018",
  consoleEdge: "#1d2836",
  surface: "#0a0e15",
  raised: "#0e141d",
  card: "#101722",
  border: "#2a3648",
  borderHi: "#405068",
  cyan: "#38bdf8",
  cyanDim: "rgba(56,189,248,.1)",
  cyanGlow: "rgba(56,189,248,.22)",
  pink: "#38bdf8",
  pinkDim: "rgba(56,189,248,.1)",
  pinkGlow: "rgba(56,189,248,.22)",
  purple: "#0ea5e9",
  white: "#ffffff",
  offwhite: "#e2e8f0",
  muted: "#94a3b8",
  dim: "#7c8aa0",
  green: "#34d399",
  gold: "#e3b341",
  red: "#f87171",
};

// Video is the only output left because the corrido music video and the TikTok
// story are the only things our own engine renders. Image, audio and utility
// only ever reached the retired A2E service, so they could only fail.
type OutputType = "video";
type VideoDurationChoice = "5" | "10" | "15";
type AspectRatioChoice = "16:9" | "9:16" | "1:1";
type AssetKey =
  | "photo" | "face_photo" | "target_media" | "source_person" | "target_video" | "garment_photo" | "person_photo" | "product_photo" | "brand_bg" | "video" | "voice_sample" | "existing_media";

interface SubType {
  id: string;
  icon: string;
  label: string;
  desc: string;
  assets: AssetKey[];
  optional?: AssetKey[];
}

type AssetSubmitPayload = {
  value: string;
  displayLabel?: string;
};

function getTranslatedSubTypes(t: TFunction): Record<OutputType, SubType[]> {
  // Only the workflows our own engines render are offered. The image
  // (generate, portrait, edit, upscale), audio (tts, clone, extract) and
  // utility (sub_remove, vid_dl) tools went out with A2E: each one launched
  // through /media-agent/chat into the retired A2E service and could only
  // fail. Add a tool back here only when a local workflow renders it.
  return {
    video: [
      { id: "corrido", icon: "", label: t('directorConsole.workflow.corridoLabel'), desc: t('directorConsole.workflow.corridoDesc'), assets: [] },
      { id: "tiktok_story", icon: "", label: t('directorConsole.workflow.storyLabel'), desc: t('directorConsole.workflow.storyDesc'), assets: [] },
      // Commercials & Ads — its own worker image on RunPod (docs/ads-tool-contract.md).
      { id: "ads", icon: "", label: t('directorConsole.workflow.adsLabel'), desc: t('directorConsole.workflow.adsDesc'), assets: [] },
      // A2E-era video workflows removed from the menu: animate, presenter,
      // make_talk, faceswap, headswap, actorswap, textovid, product, outfit.
      // Nothing powers them since the A2E retirement — restore each one only
      // when a local ComfyUI workflow backs it.
    ],
  };
}

const ASSET_DEFS: Record<AssetKey, { icon: string; label: string; hint: string; accept: string; labelKey: string; hintKey: string }> = {
  photo: { icon: "", label: "Photo to animate", hint: "Front-facing works best. Minimum 512×512px.", accept: "image/*", labelKey: "directorConsole.assets.photoLabel", hintKey: "directorConsole.assets.photoHint" },
  face_photo: { icon: "", label: "Source face photo", hint: "Front-facing, neutral expression. No sunglasses or hats.", accept: "image/*", labelKey: "directorConsole.assets.face_photoLabel", hintKey: "directorConsole.assets.face_photoHint" },
  target_media: { icon: "", label: "Target photo or video", hint: "Where the face will be placed.", accept: "image/*,video/*", labelKey: "directorConsole.assets.target_mediaLabel", hintKey: "directorConsole.assets.target_mediaHint" },
  source_person: { icon: "", label: "Source person (photo/video)", hint: "Full or upper body visible works best.", accept: "image/*,video/*", labelKey: "directorConsole.assets.source_personLabel", hintKey: "directorConsole.assets.source_personHint" },
  target_video: { icon: "", label: "Target video", hint: "MP4 preferred. Shorter clips finish faster.", accept: "video/*", labelKey: "directorConsole.assets.target_videoLabel", hintKey: "directorConsole.assets.target_videoHint" },
  garment_photo: { icon: "", label: "Photo of the garment", hint: "Flat lay or mannequin shots work best.", accept: "image/*", labelKey: "directorConsole.assets.garment_photoLabel", hintKey: "directorConsole.assets.garment_photoHint" },
  person_photo: { icon: "", label: "Photo of the person", hint: "Use a clear full-body or torso shot.", accept: "image/*", labelKey: "directorConsole.assets.person_photoLabel", hintKey: "directorConsole.assets.person_photoHint" },
  product_photo: { icon: "", label: "Product photo", hint: "Plain backgrounds or transparent PNGs are ideal.", accept: "image/*", labelKey: "directorConsole.assets.product_photoLabel", hintKey: "directorConsole.assets.product_photoHint" },
  brand_bg: { icon: "", label: "Brand background", hint: "Optional — leave blank for a default set.", accept: "image/*", labelKey: "directorConsole.assets.brand_bgLabel", hintKey: "directorConsole.assets.brand_bgHint" },
  video: { icon: "", label: "Video file", hint: "MP4 preferred.", accept: "video/*", labelKey: "directorConsole.assets.videoLabel", hintKey: "directorConsole.assets.videoHint" },
  voice_sample: { icon: "", label: "Voice sample", hint: "At least 30 seconds of clear speech.", accept: "audio/*,video/*", labelKey: "directorConsole.assets.voice_sampleLabel", hintKey: "directorConsole.assets.voice_sampleHint" },
  existing_media: { icon: "", label: "Your file", hint: "Image or video accepted. MP4 preferred for video.", accept: "image/*,video/*", labelKey: "directorConsole.assets.existing_mediaLabel", hintKey: "directorConsole.assets.existing_mediaHint" },
};

const DESCRIBE_HINTS: Record<string, string> = {
  generate: "Describe the subject, style, camera angle, lighting, and mood. More detail = tighter result.",
  portrait: "Describe the person — age, expression, features, wardrobe, lighting, and background.",
  photobook: "Choose a face image, then describe the scene, outfits, mood, and how many portraits you want.",
  social_profile_pic_studio: "Specify platforms (Instagram, LinkedIn, etc.), persona (professional, creator), and your goal.",
  edit: "Describe exactly what should change. Example: remove background, sharpen, restyle, or swap elements.",
  upscale: "Add any quality notes, or leave it simple and Director will maximize clarity.",
  animate: "Describe the motion. Example: hair gently blowing, slow smile, subtle camera drift.",
  presenter: "Write the script your presenter should deliver. Short, natural lines work best.",
  make_talk: "Write the exact words to be spoken. Commas create short pauses; periods create longer ones.",
  faceswap: "Add any swap notes, or just confirm and Director handles the rest.",
  headswap: "Add any notes, or leave it simple and confirm.",
  actorswap: "Add replacement notes, style notes, or just confirm.",
  textovid: "Describe the scene, action, camera style, and overall mood.",
  product: "Write the sales pitch. Make it punchy, clear, and benefit-focused.",
  outfit: "Describe how the garment should fit or appear on the person.",
  tts: "Write the voiceover exactly as it should be spoken.",
  clone: "Share tone or usage notes for the cloned voice.",
  extract: "Optional notes: isolate dialogue, remove music, or keep full mix.",
  sub_remove: "Optional notes, or leave blank and Director will clean the subtitles.",
  vid_dl: "Paste the public video URL you want to import.",
};

// Display-side lookups. ASSET_DEFS keeps its English `label`/`hint` because the
// [Director Brief] posted to the backend planner is a fixed-language protocol
// string; anything a person reads resolves through these instead.
const assetLabel = (t: TFunction, key: AssetKey) => t(ASSET_DEFS[key].labelKey);
const assetHint = (t: TFunction, key: AssetKey) => t(ASSET_DEFS[key].hintKey);
const describeHint = (t: TFunction, subTypeId: string) =>
  DESCRIBE_HINTS[subTypeId]
    ? t(`directorConsole.describeHints.${subTypeId}`)
    : t('directorConsole.describeHints.fallback');

function inferGalleryKind(accept: string): "image" | "video" | "all" {
  if (accept.startsWith("image/")) return "image";
  if (accept.startsWith("video/")) return "video";
  return "all";
}

function fallbackAssetSelectionLabel(t: TFunction, assetKey: AssetKey, value: string) {
  if (value.startsWith("blob:")) return t('directorConsole.ui.fileUploaded');
  if (/^https?:\/\//i.test(value)) {
    return ASSET_DEFS[assetKey].accept.startsWith("image/")
      ? t('directorConsole.ui.galleryImageSelected') : t('directorConsole.ui.galleryItemSelected');
  }
  return value.length > 40 ? `${value.slice(0, 38)}…` : value;
}

function getMissingRequiredAssets(selectedSubType: SubType, currentAssets: Record<string, string | File>) {
  return selectedSubType.assets.filter((assetKey) => {
    const val = currentAssets[assetKey];
    if (!val) return true;
    if (typeof val === "string") return !val.trim();
    return false;
  });
}

interface StoredDirectorDraft {
  outputType: OutputType;
  subTypeId: string;
  assets: Record<string, string | File>;
  description: string;
  videoDuration?: VideoDurationChoice | null;
  aspectRatio?: AspectRatioChoice | null;
  savedAt: string;
}

const VIDEO_DURATION_OPTIONS: Array<{ value: VideoDurationChoice; label: string }> = [
  { value: "5", label: "5s" },
  { value: "10", label: "10s" },
  { value: "15", label: "15s" },
];

const VIDEO_ASPECT_OPTIONS: Array<{ value: AspectRatioChoice; label: string; hint: string }> = [
  { value: "16:9", label: "16:9", hint: "Landscape" },
  { value: "9:16", label: "9:16", hint: "Vertical" },
  { value: "1:1", label: "1:1", hint: "Square" },
];

function findSubTypeById(subTypeId: string, subTypes: Record<OutputType, SubType[]>): { outputType: OutputType; subType: SubType } | null {
  const outputTypes = Object.keys(subTypes) as OutputType[];
  for (const outputType of outputTypes) {
    const subType = subTypes[outputType].find((entry) => entry.id === subTypeId);
    if (subType) return { outputType, subType };
  }
  return null;
}

function readStoredDirectorDraft(subTypes: Record<OutputType, SubType[]>): StoredDirectorDraft | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredDirectorDraft>;
    if (typeof parsed.subTypeId !== "string") return null;
    const resolved = findSubTypeById(parsed.subTypeId, subTypes);
    if (!resolved) return null;
    if (parsed.outputType !== resolved.outputType) return null;
    if (typeof parsed.description !== "string" || !parsed.description.trim()) return null;

    const assets =
      parsed.assets && typeof parsed.assets === "object" ? Object.fromEntries(
          Object.entries(parsed.assets).filter(
            ([key, value]) => typeof key === "string" && typeof value === "string" && value.trim().length > 0,
          ),
        )
        : {};

    return {
      outputType: resolved.outputType,
      subTypeId: resolved.subType.id,
      assets,
      description: parsed.description.trim(),
      videoDuration:
        parsed.videoDuration === "5" || parsed.videoDuration === "10" || parsed.videoDuration === "15" ? parsed.videoDuration
          : null,
      aspectRatio:
        parsed.aspectRatio === "16:9" || parsed.aspectRatio === "9:16" || parsed.aspectRatio === "1:1" ? parsed.aspectRatio
          : null,
      savedAt: typeof parsed.savedAt === "string" ? parsed.savedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

interface Msg {
  id: number;
  from: "director" | "user";
  text?: string;
  widget?: ReactNode;
  wide?: boolean;
}

let nextMsgId = 0;
const uid = () => ++nextMsgId + Math.random();

function DAvatar({ className }: { className?: string }) {
  return (
    <div
      className={className}
      style={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        flexShrink: 0,
        background: `linear-gradient(135deg,${P.pink},${P.purple})`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        color: P.white,
        boxShadow: `0 0 16px ${P.pinkGlow}`,
        fontFamily: "'Rajdhani', sans-serif",
        letterSpacing: 1,
      }}
    > D
    </div>
  );
}

function Dots() {
  return (
    <div style={{ display: "flex", gap: 5, padding: "3px 0" }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: `linear-gradient(135deg,${P.cyan},${P.purple})`,
            animation: `ldc-blink 1.3s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

function Bubble({ msg }: { msg: Msg }) {
  const isDirector = msg.from === "director";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: isDirector ? "flex-start" : "flex-end",
        alignItems: "flex-end",
        gap: 10,
        animation: "ldc-fadein .3s ease",
      }}
    >
      {isDirector && <DAvatar className="ldc-bubble-avatar" />}
      {/* ldc-bubble / data-from: the phone rules in the console stylesheet. */}
      <div
        className="ldc-bubble"
        data-from={msg.from}
        style={{
          maxWidth: msg.wide ? "96%" : "76%",
          borderRadius: 14,
          padding: "10px 14px",
          fontSize: 13,
          lineHeight: 1.65,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "system-ui, sans-serif",
          ...(isDirector
            ? { background: P.raised, border: `1px solid ${P.border}`, borderBottomLeftRadius: 3, color: P.offwhite }
            : { background: `linear-gradient(135deg,rgba(56,189,248,.18),rgba(191,64,255,.14))`, border: `1px solid rgba(56,189,248,.3)`, borderBottomRightRadius: 3, color: P.white }),
        }}
      >
        {msg.text && (
          <div
            dangerouslySetInnerHTML={{
              __html: msg.text.replace(/\*\*([^*]+)\*\*/g, "<strong style='color:#fff;font-weight:700'>$1</strong>"),
            }}
          />
        )}
        {msg.widget && <div style={{ marginTop: msg.text ? 10 : 0 }}>{msg.widget}</div>}
      </div>
    </div>
  );
}

function OutputPicker({ onSelect }: { onSelect: (type: OutputType) => void }) {
  const { t } = useTranslation();
  // One card now: Video opens the corrido and TikTok story choices. The image,
  // audio and utility cards led only to the retired A2E service.
  const options: Array<{ id: OutputType; icon: string; label: string; desc: string; color: string; glow: string }> = [
    { id: "video", icon: "", label: t('directorConsole.ui.outputVideo'), desc: t('directorConsole.categories.videoDesc'), color: P.pink, glow: P.pinkGlow },
  ];
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginTop: 4 }}>
      {options.map((option) => (
        <button
          key={option.id}
          data-testid={`landing-director-output-${option.id}`}
          onMouseEnter={() => setHovered(option.id)}
          onMouseLeave={() => setHovered(null)}
          onClick={() => onSelect(option.id)}
          style={{
            background: hovered === option.id ? P.card : P.surface,
            border: `1px solid ${hovered === option.id ? option.color : P.border}`,
            borderRadius: 12,
            padding: "16px 14px",
            cursor: "pointer",
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            transition: "all .18s",
            boxShadow: hovered === option.id ? `0 6px 24px ${option.glow}` : "none",
            transform: hovered === option.id ? "translateY(-2px)" : "none",
          }}
        >
          <span style={{ fontSize: 24, lineHeight: 1 }}>{option.icon}</span>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 2, color: hovered === option.id ? option.color : P.offwhite }}>
            {option.label}
          </span>
          <span style={{ fontSize: 12, color: P.muted, lineHeight: 1.5 }}>{option.desc}</span>
        </button>
      ))}
    </div>
  );
}

// The Director's opening offer: the flagship workflows as big cards, plus a
// fallback into the classic output-type picker. This is the FIRST thing every
// visitor sees, so every live product is always on the table.
function WorkflowPicker({ onCorrido, onStory, onAds, onOther }: { onCorrido: () => void; onStory: () => void; onAds: () => void; onOther: () => void }) {
  const { t } = useTranslation();
  const cards = [
    {
      id: "corrido", icon: "", label: t('directorConsole.workflow.corridoLabel'),
      desc: t('directorConsole.workflow.corridoDesc'),
      color: P.pink, glow: P.pinkGlow, onClick: onCorrido,
      // The four pipeline proof frames moved to the landing-page sales pitch
      // (WhyCorridoSection collage, top of page) — they sell there; here they
      // cramped the picker (user directive 2026-07-22).
      previews: [] as string[],
    },
    {
      id: "tiktok_story", icon: "", label: t('directorConsole.workflow.storyLabel'),
      desc: t('directorConsole.workflow.storyDesc'),
      color: P.cyan, glow: P.cyanGlow, onClick: onStory,
      previews: [] as string[],
    },
    {
      id: "ads", icon: "", label: t('directorConsole.workflow.adsLabel'),
      desc: t('directorConsole.workflow.adsDesc'),
      color: "#22c55e", glow: "rgba(34,197,94,.22)", onClick: onAds,
      previews: [] as string[],
    },
  ];
  const [hovered, setHovered] = useState<string | null>(null);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
      {cards.map((card) => (
        <button
          key={card.id}
          data-testid={`landing-director-workflow-${card.id}`}
          onMouseEnter={() => setHovered(card.id)}
          onMouseLeave={() => setHovered(null)}
          onClick={card.onClick}
          style={{
            background: hovered === card.id ? P.card : P.surface,
            border: `1px solid ${hovered === card.id ? card.color : P.border}`,
            borderRadius: 12,
            padding: "14px 16px",
            cursor: "pointer",
            textAlign: "left",
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            transition: "all .18s",
            boxShadow: hovered === card.id ? `0 6px 24px ${card.glow}` : "none",
            transform: hovered === card.id ? "translateY(-2px)" : "none",
          }}
        >
          <span style={{ fontSize: 26, lineHeight: 1, flexShrink: 0 }}>{card.icon}</span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: "block", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 1.5, color: hovered === card.id ? card.color : P.offwhite }}>
              {card.label}
            </span>
            <span style={{ display: "block", fontSize: 13, color: P.muted, lineHeight: 1.5, marginTop: 3 }}>{card.desc}</span>
            {card.previews.length > 0 && (
              <span style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                {card.previews.map((src) => (
                  <img
                    key={src}
                    src={src}
                    alt="" loading="lazy" style={{ width: 92, height: 60, objectFit: "cover", borderRadius: 6, border: `1px solid ${P.border}`, flexShrink: 0 }}
                  />
                ))}
              </span>
            )}
          </span>
        </button>
      ))}
      <button
        data-testid="landing-director-workflow-other" onClick={onOther}
        style={{ background: "transparent", border: `1px dashed ${P.border}`, borderRadius: 10, padding: "9px 14px", cursor: "pointer", textAlign: "left", fontSize: 13, color: P.muted }}
      >
        {t('directorConsole.workflow.other')}
      </button>
    </div>
  );
}

function SubTypePicker({ subs, onSelect }: { subs: SubType[]; onSelect: (subType: SubType) => void }) {
  const [hovered, setHovered] = useState<string | null>(null);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
      {subs.map((sub) => (
        <button
          key={sub.id}
          data-testid={`landing-director-subtype-${sub.id}`}
          onMouseEnter={() => setHovered(sub.id)}
          onMouseLeave={() => setHovered(null)}
          onClick={() => onSelect(sub)}
          style={{
            background: hovered === sub.id ? P.card : P.surface,
            border: `1px solid ${hovered === sub.id ? P.cyan : P.border}`,
            borderRadius: 11,
            padding: "12px 14px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 12,
            transition: "all .16s",
            boxShadow: hovered === sub.id ? `0 4px 18px ${P.cyanGlow}` : "none",
            transform: hovered === sub.id ? "translateX(4px)" : "none",
          }}
        >
          <span style={{ fontSize: 20, width: 28, textAlign: "center", flexShrink: 0 }}>{sub.icon}</span>
          <div style={{ flex: 1, textAlign: "left" }}>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, fontWeight: 700, color: hovered === sub.id ? P.cyan : P.white, marginBottom: 2 }}>
              {sub.label}
            </div>
            <div style={{ fontSize: 12, color: P.muted, lineHeight: 1.5 }}>{sub.desc}</div>
          </div>
          <span style={{ color: hovered === sub.id ? P.cyan : P.dim, fontSize: 15, flexShrink: 0 }}>→</span>
        </button>
      ))}
    </div>
  );
}

function AssetInput({
  assetKey,
  optional,
  onSubmit,
  onSkip,
}: {
  assetKey: AssetKey;
  optional?: boolean;
  onSubmit: (payload: AssetSubmitPayload) => void;
  onSkip?: () => void;
}) {
  const { t } = useTranslation();
  const def = ASSET_DEFS[assetKey];
  const uploadActionLabel = def.accept.startsWith("image/") ? t('directorConsole.ui.uploadImage') : t('directorConsole.ui.uploadFile');
  const requiredSelectionMessage = def.accept.startsWith("image/")
    ? t('directorConsole.ui.needImage') : t('directorConsole.ui.needFile');
  const [fileName, setFileName] = useState("");
  const [selectionLabel, setSelectionLabel] = useState("");
  const [tipsOpen, setTipsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    setError(null);
    if (assetKey === "photo") {
      if (!file.type.startsWith("image/")) {
        setError(t('directorConsole.ui.needPngJpg'));
        return;
      }
      let dims: { width: number; height: number } | null = null;
      try {
        dims = await new Promise<{ width: number; height: number }>((resolve, reject) => {
          const objectUrl = URL.createObjectURL(file);
          const img = new Image();
          img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve({ width: img.width, height: img.height });
          };
          img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error("image_decode_failed"));
          };
          img.src = objectUrl;
        });
      } catch {
        dims = null;
      }
      if (dims && (dims.width < 512 || dims.height < 512)) {
        setError(t('directorConsole.ui.imageTooSmall'));
        return;
      }
    }
    setFileName(file.name);
    const uploadedLabel = t('directorConsole.ui.uploadedFile', { name: file.name });
    setSelectionLabel(uploadedLabel);
    onSubmit({ value: URL.createObjectURL(file), displayLabel: uploadedLabel, file } as { value: string; displayLabel: string; file: File });
  };

  const handleGalleryPick = (item: UserMediaGalleryItem) => {
    setError(null);
    setFileName(item.name);
    const galleryLabel = t('directorConsole.ui.galleryItem', { name: item.name });
    setSelectionLabel(galleryLabel);
    onSubmit({ value: item.url, displayLabel: galleryLabel });
  };

  return (
    <>
      <UserMediaGalleryDialog
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        mediaKind={inferGalleryKind(def.accept)}
        title={t('directorConsole.ui.chooseFromGallery', { label: assetLabel(t, assetKey).toLowerCase() })}
        description={t('directorConsole.ui.galleryHasMatches')} emptyMessage={t('directorConsole.ui.galleryEmpty')} onSelect={handleGalleryPick}
      />
      <div style={{ background: P.card, border: `1px solid ${P.borderHi}`, borderRadius: 12, padding: 14, animation: "ldc-fadein .22s ease" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 16 }}>{def.icon}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: P.white, flex: 1, fontFamily: "'Rajdhani', sans-serif", letterSpacing: 0.5 }}>{assetLabel(t, assetKey)}</span>
          {optional && <span style={{ fontSize: 11, letterSpacing: 1.5, color: P.muted, border: `1px solid ${P.border}`, padding: "2px 6px", borderRadius: 4 }}>{t('directorConsole.ui.optional')}</span>}
          <button
            type="button" onClick={() => setTipsOpen((value) => !value)}
            style={{ background: "transparent", border: `1px solid ${P.border}`, color: P.muted, fontSize: 11, padding: "2px 8px", borderRadius: 4, cursor: "pointer" }}
          >
            {tipsOpen ? t('directorConsole.ui.hideTip') : t('directorConsole.ui.showTip')}
          </button>
        </div>
        {tipsOpen && <div style={{ background: P.bg, border: `1px solid ${P.border}`, borderRadius: 8, padding: "8px 10px", marginBottom: 10, fontSize: 12, color: P.muted, lineHeight: 1.6 }}>{assetHint(t, assetKey)}</div>}
        <div style={{ background: P.bg, border: `1px solid ${P.border}`, borderRadius: 10, padding: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: P.muted, lineHeight: 1.6, marginBottom: 10 }}>{t('directorConsole.ui.uploadOrGallery')}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button" data-testid={`landing-director-upload-${assetKey}`}
              onClick={() => inputRef.current?.click()}
              style={{ background: P.raised, border: `1px dashed ${selectionLabel ? P.green : P.borderHi}`, color: selectionLabel ? P.green : P.muted, padding: "7px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              {uploadActionLabel}
            </button>
            <button
              type="button" data-testid={`landing-director-gallery-${assetKey}`}
              onClick={() => setGalleryOpen(true)}
              style={{ background: "transparent", border: `1px solid ${P.borderHi}`, color: P.offwhite, padding: "7px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}
            >{t('directorConsole.ui.chooseFromGalleryBtn')}
            </button>
            <input
              data-testid={`landing-director-upload-input-${assetKey}`}
              ref={inputRef}
              type="file" data-consent-tool="corrido" accept={def.accept}
              style={{ display: "none" }}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];
                if (!file) return;
                void handleFileSelect(file);
              }}
            />
          </div>
          {selectionLabel ? (
            <div style={{ fontSize: 12, color: P.green, marginTop: 10 }}>
              ✓ {selectionLabel}
              {fileName ? ` (${fileName})` : ""}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: P.pink, marginTop: 10 }}>
              {requiredSelectionMessage}
            </div>
          )}
        </div>
      {error && (
        <div style={{ fontSize: 12, color: P.pink, marginTop: 6 }}>{error}</div>
      )}
      {(optional || onSkip) && (
        <button type="button" onClick={onSkip} style={{ background: "transparent", border: "none", color: P.muted, fontSize: 12, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>{t('directorConsole.ui.skipForNow')}
        </button>
      )}
      </div>
    </>
  );
}

function DescribeInput({ hint, onSubmit }: { hint: string; onSubmit: (value: string) => void }) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  return (
    <div style={{ background: P.card, border: `1px solid ${P.borderHi}`, borderRadius: 12, padding: 14, animation: "ldc-fadein .22s ease" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: P.white, marginBottom: 4, fontFamily: "'Rajdhani', sans-serif", letterSpacing: 0.5 }}>{t('directorConsole.conversation.yourBriefToDirector')}</div>
      <div style={{ fontSize: 12, color: P.muted, marginBottom: 10, lineHeight: 1.6 }}>{hint}</div>
      <textarea
        data-testid="landing-director-description-input" value={value}
        onChange={(event) => setValue(event.target.value)}
        rows={4}
        placeholder={t('directorConsole.ui.typeHere')} style={{ width: "100%", background: P.bg, border: `1px solid ${P.borderHi}`, color: P.offwhite, borderRadius: 9, padding: "10px 12px", fontSize: 13, resize: "vertical", outline: "none", minHeight: 80, lineHeight: 1.6, boxSizing: "border-box" }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <button
          type="button" data-testid="landing-director-review-button" disabled={value.trim().length < 3}
          onClick={() => onSubmit(value.trim())}
          style={{ background: value.trim().length > 2 ? `linear-gradient(135deg,${P.pink},${P.purple})` : "rgba(255,255,255,.06)", color: value.trim().length > 2 ? P.white : P.muted, border: "none", padding: "8px 20px", borderRadius: 8, fontSize: 12, fontWeight: 700, letterSpacing: 1, cursor: value.trim().length > 2 ? "pointer" : "default", fontFamily: "'Rajdhani', sans-serif" }}
        >{t('directorConsole.ui.reviewConfirm')}
        </button>
      </div>
    </div>
  );
}

function VideoSpecsCard({
  duration,
  aspectRatio,
  onDurationChange,
  onAspectRatioChange,
}: {
  duration: VideoDurationChoice | null;
  aspectRatio: AspectRatioChoice | null;
  onDurationChange: (value: VideoDurationChoice) => void;
  onAspectRatioChange: (value: AspectRatioChoice) => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ background: P.surface, border: `1px solid ${P.border}`, borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 12, letterSpacing: 2.5, color: P.cyan, fontWeight: 700, marginBottom: 12, fontFamily: "'Rajdhani', sans-serif" }}>{t('directorConsole.ui.videoSpecs')}
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: P.offwhite, marginBottom: 8 }}>{t('directorConsole.ui.chooseDuration')}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {VIDEO_DURATION_OPTIONS.map((option) => {
            const active = duration === option.value;
            return (
              <button
                key={option.value}
                type="button" data-testid={`landing-director-duration-${option.value}`}
                onClick={() => onDurationChange(option.value)}
                style={{
                  background: active ? `linear-gradient(135deg,${P.pink},${P.purple})` : P.bg,
                  color: active ? P.white : P.offwhite,
                  border: `1px solid ${active ? P.pink : P.borderHi}`,
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  minWidth: 60,
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, color: P.offwhite, marginBottom: 8 }}>{t('directorConsole.ui.chooseAspect')}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: 8 }}>
          {VIDEO_ASPECT_OPTIONS.map((option) => {
            const active = aspectRatio === option.value;
            return (
              <button
                key={option.value}
                type="button" data-testid={`landing-director-aspect-${option.value.replace(":", "-")}`}
                onClick={() => onAspectRatioChange(option.value)}
                style={{
                  background: active ? P.card : P.bg,
                  color: active ? P.white : P.offwhite,
                  border: `1px solid ${active ? P.cyan : P.borderHi}`,
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 700 }}>{option.label}</div>
                <div style={{ fontSize: 11, color: active ? P.white : P.muted, marginTop: 2 }}>{option.hint}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ConfirmCard({
  outputType,
  subType,
  assets,
  assetDisplayLabels,
  description,
  isAuthenticated,
  videoDuration,
  aspectRatio,
  onDurationChange,
  onAspectRatioChange,
  onLaunch,
  onAdjust,
}: {
  outputType: OutputType;
  subType: SubType;
  assets: Record<string, string | File>;
  assetDisplayLabels: Record<string, string>;
  description: string;
  isAuthenticated: boolean;
  videoDuration: VideoDurationChoice | null;
  aspectRatio: AspectRatioChoice | null;
  onDurationChange: (value: VideoDurationChoice) => void;
  onAspectRatioChange: (value: AspectRatioChoice) => void;
  onLaunch: (duration: VideoDurationChoice | null, aspectRatio: AspectRatioChoice | null) => void;
  onAdjust: () => void;
}) {
  const { t } = useTranslation();
  const [localDuration, setLocalDuration] = useState<VideoDurationChoice | null>(videoDuration);
  const [localAspectRatio, setLocalAspectRatio] = useState<AspectRatioChoice | null>(aspectRatio);

  useEffect(() => {
    setLocalDuration(videoDuration);
  }, [videoDuration]);

  useEffect(() => {
    setLocalAspectRatio(aspectRatio);
  }, [aspectRatio]);

  const needsVideoSpecs = outputType === "video";
  const videoSpecsReady = !needsVideoSpecs || (Boolean(localDuration) && Boolean(localAspectRatio));
  const requiredAssetsMissing = getMissingRequiredAssets(subType, assets).length > 0;
  const canBuildSummary = videoSpecsReady && !requiredAssetsMissing;
  const rows = [
    { key: t('directorConsole.ui.rowOutput'), value: t(`directorConsole.ui.output${outputType.charAt(0).toUpperCase()}${outputType.slice(1)}`) },
    { key: t('directorConsole.ui.rowType'), value: subType.label },
    ...(outputType === "video" ? [
        { key: t('directorConsole.ui.rowDuration'), value: localDuration ? `${localDuration}s` : t('directorConsole.ui.chooseBeforeLaunch') },
        { key: t('directorConsole.ui.rowAspect'), value: localAspectRatio || t('directorConsole.ui.chooseBeforeLaunch') },
      ]
      : []),
    ...Object.entries(assets)
      .filter(([, value]) => value)
      .map(([key, value]) => ({
        key: ASSET_DEFS[key as AssetKey] ? assetLabel(t, key as AssetKey) : key,
        value: assetDisplayLabels[key] || fallbackAssetSelectionLabel(t, key as AssetKey, typeof value === "string" ? value : value.name),
      })),
    { key: t('directorConsole.ui.rowBrief'), value: description.length > 60 ? `${description.slice(0, 58)}…` : description },
  ];

  return (
    <div style={{ background: P.card, border: `1px solid rgba(56,189,248,.25)`, borderRadius: 13, padding: 16, animation: "ldc-fadein .22s ease" }}>
      <div style={{ fontSize: 11, letterSpacing: 3, color: P.cyan, fontWeight: 700, marginBottom: 14, fontFamily: "'Rajdhani', sans-serif" }}>{t('directorConsole.ui.yourOrder')}</div>
      {rows.map((row, index) => (
        <div key={`${row.key}-${index}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", gap: 12 }}>
            <span style={{ fontSize: 11, letterSpacing: 1.2, color: P.muted, textTransform: "uppercase", flexShrink: 0 }}>{row.key}</span>
            <span style={{ fontSize: 12, color: P.white, fontWeight: 600, textAlign: "right", maxWidth: "65%" }}>{row.value}</span>
          </div>
          {index < rows.length - 1 && <div style={{ height: 1, background: P.border }} />}
        </div>
      ))}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
        {needsVideoSpecs && (
          <VideoSpecsCard
            duration={localDuration}
            aspectRatio={localAspectRatio}
            onDurationChange={(value) => {
              setLocalDuration(value);
              onDurationChange(value);
            }}
            onAspectRatioChange={(value) => {
              setLocalAspectRatio(value);
              onAspectRatioChange(value);
            }}
          />
        )}
        {requiredAssetsMissing ? (
          <div style={{ background: "rgba(56,189,248,.08)", border: `1px solid ${P.pink}`, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: P.offwhite, lineHeight: 1.5 }}>{t('directorConsole.ui.needImage')}</div>
        ) : null}
        {isAuthenticated ? (
          <button
            type="button" data-testid="landing-director-launch-button" onClick={() => onLaunch(localDuration, localAspectRatio)}
            disabled={!canBuildSummary}
            style={{ background: canBuildSummary ? `linear-gradient(135deg,${P.pink},${P.purple})` : "rgba(255,255,255,.08)", color: canBuildSummary ? P.white : P.muted, border: "none", borderRadius: 10, padding: "13px 20px", fontSize: 14, fontWeight: 700, letterSpacing: 2, cursor: canBuildSummary ? "pointer" : "default", fontFamily: "'Rajdhani', sans-serif", boxShadow: canBuildSummary ? `0 6px 28px ${P.pinkGlow}` : "none" }}
          >
            {requiredAssetsMissing ? t('directorConsole.ui.chooseRequiredImage') : videoSpecsReady ? t('directorConsole.ui.reviewFinalSummary') : t('directorConsole.ui.chooseVideoSpecs')}
          </button>
        ) : (
          <div style={{ background: P.surface, border: `1px solid ${P.border}`, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: P.offwhite, marginBottom: 12, lineHeight: 1.6 }}>{t('directorConsole.ui.allSet')} <strong style={{ color: P.white }}>{t('directorConsole.ui.signUpOrLogIn')}</strong> {t('directorConsole.ui.allSetTail')}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link data-testid="landing-director-signup-link" to="/signup" style={{ flex: 1, minWidth: 120, background: `linear-gradient(135deg,${P.pink},${P.purple})`, color: P.white, borderRadius: 9, padding: "10px 0", fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 18px ${P.pinkGlow}` }}>{t('directorConsole.ui.signUp')}
              </Link>
              <Link data-testid="landing-director-login-link" to="/login" style={{ flex: 1, minWidth: 120, background: "transparent", color: P.muted, border: `1px solid ${P.border}`, borderRadius: 9, padding: "10px 0", fontSize: 12, fontWeight: 600, letterSpacing: 1, textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>{t('directorConsole.ui.logIn')}
              </Link>
            </div>
            {/* Or Google, right here (2026-09-25): signing in on the spot
                keeps this order on screen, where the links above leave it. */}
            <div style={{ marginTop: 10 }}>
              <GoogleSignInButton source="director_console" className="rounded-[9px] text-xs" />
            </div>
          </div>
        )}
        <button type="button" data-testid="landing-director-adjust-button" onClick={onAdjust} style={{ background: "transparent", border: `1px solid ${P.border}`, color: P.muted, borderRadius: 9, padding: "9px 14px", fontSize: 13, cursor: "pointer", letterSpacing: 1, fontFamily: "'Rajdhani', sans-serif" }}>
          {t('directorConsole.ui.adjust')}
        </button>
      </div>
    </div>
  );
}

function FinalSummaryCard({
  plan,
  canLaunch,
  isLaunching,
  launchStateText,
  onLaunch,
}: {
  plan: DirectorPlan;
  canLaunch: boolean;
  isLaunching: boolean;
  launchStateText: string | null;
  onLaunch: () => void;
}) {
  const { t } = useTranslation();
  const total = formatPlanCost(
    t,
    Number(plan.totals?.estimated_credits || 0),
    Number(plan.totals?.estimated_cost?.amount_cents || 0),
    String(plan.totals?.estimated_cost?.currency || "MXN"),
  );
  return (
    <div style={{ background: P.card, border: `1px solid rgba(56,189,248,.25)`, borderRadius: 13, padding: 16, animation: "ldc-fadein .22s ease" }}>
      <div style={{ fontSize: 11, letterSpacing: 3, color: P.cyan, fontWeight: 700, marginBottom: 12, fontFamily: "'Rajdhani', sans-serif" }}>{t('directorConsole.ui.finalSummary')}
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {plan.steps.map((step, index) => (
          <div key={step.step_id || `step-${index}`} style={{ background: P.surface, border: `1px solid ${P.border}`, borderRadius: 10, padding: "10px 12px" }}>
            <div style={{ fontSize: 13, color: P.white, fontWeight: 700 }}>
              {index + 1}. {step.tool_label}
            </div>
            <div style={{ fontSize: 12, color: P.muted, marginTop: 4 }}>
              {formatPlanCost(
                t,
                Number(step.estimated_credits || 0),
                Number(step.estimated_cost?.amount_cents || 0),
                String(step.estimated_cost?.currency || "MXN"),
              )}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "rgba(56,189,248,.08)", border: `1px solid ${P.cyanDim}` }}>
        <div style={{ fontSize: 13, color: P.white, fontWeight: 700 }}>{t('directorConsole.ui.totalEstimated', { total })}
        </div>
        <div style={{ fontSize: 12, color: P.muted, marginTop: 4 }}>
          {plan.note || t('directorConsole.ui.nothingLaunched')}
        </div>
      </div>
      {launchStateText ? (
        <div style={{ marginTop: 10, fontSize: 13, color: P.cyan }}>
          {launchStateText}
        </div>
      ) : null}
      <button
        type="button" data-testid="landing-director-final-launch-button" onClick={onLaunch}
        disabled={!canLaunch || isLaunching}
        style={{
          marginTop: 12,
          width: "100%",
          background: canLaunch && !isLaunching ? `linear-gradient(135deg,${P.pink},${P.purple})` : "rgba(255,255,255,.08)",
          color: canLaunch && !isLaunching ? P.white : P.muted,
          border: "none",
          borderRadius: 10,
          padding: "13px 20px",
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 2,
          cursor: canLaunch && !isLaunching ? "pointer" : "default",
          fontFamily: "'Rajdhani', sans-serif",
          boxShadow: canLaunch && !isLaunching ? `0 6px 28px ${P.pinkGlow}` : "none" }}
      >
        {isLaunching ? t('directorConsole.ui.launching') : t('directorConsole.ui.launchJob')}
      </button>
    </div>
  );
}

function ProgressWidget({
  label,
  progress,
  detail,
}: {
  label: string;
  progress: number;
  detail?: string;
}) {
  return (
    <div style={{ background: P.card, border: `1px solid ${P.border}`, borderRadius: 11, padding: 14 }}>
      <div style={{ fontSize: 13, color: P.offwhite, marginBottom: 8 }}>{label}</div>
      <div style={{ height: 4, background: P.dim, borderRadius: 2, overflow: "hidden", marginBottom: 5 }}>
        <div style={{ height: "100%", width: `${progress}%`, borderRadius: 2, background: `linear-gradient(90deg,${P.cyan},${P.pink})`, transition: "width .6s ease", boxShadow: `0 0 10px ${P.cyanGlow}` }} />
      </div>
      <div style={{ fontSize: 11, color: P.muted, textAlign: "right", letterSpacing: 1 }}>{progress}%</div>
      {detail ? <div style={{ fontSize: 12, color: P.muted, marginTop: 8 }}>{detail}</div> : null}
    </div>
  );
}

function FailedWidget({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ background: "rgba(56,189,248,.08)", border: `1px solid ${P.pink}`, borderRadius: 11, padding: 14 }}>
      <div style={{ fontSize: 12, color: P.pink, letterSpacing: 1.3, fontWeight: 700, marginBottom: 8 }}>{t('directorConsole.ui.jobFailed')}</div>
      <div style={{ fontSize: 13, color: P.offwhite, marginBottom: 10 }}>{message}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button" onClick={onRetry}
          style={{ borderRadius: 8, border: `1px solid ${P.border}`, background: "transparent", color: P.offwhite, padding: "8px 10px", fontSize: 13, cursor: "pointer" }}
        >{t('directorConsole.ui.tryAgain')}
        </button>
        <a
          href="/chopshop" style={{ textDecoration: "none", borderRadius: 8, border: `1px solid ${P.border}`, color: P.offwhite, padding: "8px 10px", fontSize: 13 }}
        >{t('directorConsole.ui.editAndRerun')}
        </a>
      </div>
    </div>
  );
}

function CompletedAwaitingMediaWidget() {
  const { t } = useTranslation();
  return (
    <div style={{ background: "rgba(52,211,153,.08)", border: `1px solid rgba(52,211,153,.3)`, borderRadius: 11, padding: 14 }}>
      <div style={{ fontSize: 12, color: P.green, letterSpacing: 1.3, fontWeight: 700, marginBottom: 8 }}>{t('directorConsole.ui.completed')}</div>
      <div style={{ fontSize: 13, color: P.offwhite, marginBottom: 10 }}>{t('directorConsole.ui.mediaSyncing')}</div>
      <a href="/chopshop" style={{ textDecoration: "none", borderRadius: 8, border: `1px solid ${P.border}`, color: P.offwhite, padding: "8px 10px", fontSize: 13 }}>{t('directorConsole.ui.openInChopShop')}
      </a>
    </div>
  );
}

// Chat keyword routing. A kids' picture book is its own product with its own
// page, so these requests get sent there instead of into a console workflow.
// EN: storybook / story book, picture book, kids' / children's book.
// ES: cuento(s), libro(s) infantil(es), libro para (los / mis) niños.
// Neither "storyboard" nor a bare "story" matches.
const KIDS_STORYBOOK_RE = /\bstory[\s-]?books?\b|\bpicture[\s-]?books?\b|\b(?:kids?|child(?:ren)?s?)(?:['’]s?)?[\s-]+books?\b|\bcuentos?\b|\blibros?\s+infantil(?:es)?\b|\blibros?\s+para\s+(?:(?:los|las|mis?|tus?)\s+)?ni[ñn][oa]s?\b/i;
// "cuento" is also the verb contar ("te cuento que…" = "let me tell you…"),
// which is everyday chat, so those uses are dropped before looking for the noun.
const CONTAR_VERB_RE = /\b(?:te|le|les|me|nos|se|lo|yo|no)\s+cuento\b/gi;
const TIKTOK_RE = /\btik\s*tok\b/i;

function isKidsStorybookRequest(text: string): boolean {
  if (TIKTOK_RE.test(text)) return false; // naming TikTok still means TikTok
  return KIDS_STORYBOOK_RE.test(text.replace(CONTAR_VERB_RE, " "));
}

// Commercials & Ads cues. "ad" and "commercial" only count as the THING to
// make: after an article/possessive ("an ad", "a commercial", "un comercial"),
// with a kind in front ("video ad", "TV commercial"), or followed by what it
// is for ("ads for my shop"). The bare words are left out on purpose: a
// corrido customer typing "make it more commercial" (or "más comercial") is
// editing their song, not asking for another tool. promo / advert /
// advertisement / anuncio / comerciales (the plural noun) are unambiguous.
const ADS_REQUEST_RE = /\b(?:an?|my|our|the|this|some|new)\s+(?:(?:video|image|photo|picture|tv|radio|social|instagram|facebook|tiktok|product|promo|short|quick|little|small)\s+)?(?:ads?|commercials?)\b|\b(?:video|image|photo|picture|tv|social|product)\s+(?:ads?|commercials?)\b|\b(?:ads?|commercials?)\s+(?:for|about)\b|\bpromos?\b|\bpromotional\s+videos?\b|\badverts?\b|\badvertisements?\b|\banuncios?\b|\bcomerciales\b|\b(?:un|el|mi|nuestro|tu)\s+comercial\b/i;
// A short, explicit ask for the tool ("make an ad", "quiero un anuncio"). It is
// the only way the ads route fires while another workflow is open.
const ADS_REQUEST_VERB_RE = /\b(?:make|create|build|want|need|start|open|switch|try|do|hacer|haz|hazme|crear|crea|cr[eé]ame|quiero|necesito|empezar|abrir|abre|cambiar|probar)\b/i;
const SHORT_TOOL_REQUEST_WORDS = 6;

function isAdsRequest(text: string): boolean {
  return ADS_REQUEST_RE.test(text);
}

function isShortAdsToolRequest(text: string): boolean {
  const words = text.trim().split(/\s+/).filter(Boolean);
  return words.length <= SHORT_TOOL_REQUEST_WORDS && ADS_REQUEST_VERB_RE.test(text) && ADS_REQUEST_RE.test(text);
}

/** The studio-bus service a workflow (or console subtype) id renders on. */
function workflowService(id: string | null | undefined): StudioService {
  if (id === "tiktok_story") return "story";
  if (id === "ads") return "ads";
  return "corrido";
}

function StorybookLink() {
  const { t } = useTranslation();
  return (
    <Link
      to="/storybook" data-testid="landing-director-storybook-link"
      onClick={() => trackEvent("storybook_cta_clicked", { source: "director_console" })}
      style={{ display: "inline-block", textDecoration: "none", borderRadius: 8, border: `1px solid ${P.border}`, color: P.offwhite, padding: "8px 10px", fontSize: 13 }}
    >{t('storybook.cta')}
    </Link>
  );
}

function ResultWidget({
  url,
  conversationId,
  jobId,
  sessionId,
  authToken,
  baseUrl,
  routePrefix,
}: {
  url: string;
  conversationId?: string | null;
  jobId?: string | null;
  sessionId?: string;
  authToken?: string;
  baseUrl?: string;
  routePrefix?: string;
}) {
  const { t } = useTranslation();
  const isVideo = /\.(mp4|webm|mov)/i.test(url) || url.includes("video");
  const [rating, setRating] = useState<number>(0);
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
  const canSubmit = rating >= 1 && review.trim().length >= 12 && !submitting && !submittedMessage;
  const doDownload = useCallback(() => {
    const link = document.createElement("a");
    link.href = url;
    link.download = "chopshop-result";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [url]);
  const doCopy = useCallback(async () => {
    await navigator.clipboard.writeText(url);
    setDeleteMessage(t('directorConsole.ui.resultLinkCopied'));
  }, [url]);
  const doShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: t('directorConsole.ui.chopshopResult'), url });
        setDeleteMessage(t('directorConsole.ui.shared'));
        return;
      } catch (error) {
        void error;
      }
    }
    await navigator.clipboard.writeText(url);
    setDeleteMessage(t('directorConsole.ui.shareUnavailable'));
  }, [url]);
  const doDelete = useCallback(async () => {
    try {
      const response = await api.deleteUserMediaRecord({ id: jobId || undefined, job_id: jobId || undefined, url });
      if (!response.success) throw new Error(response.error || t('directorConsole.ui.deleteFailed'));
      setDeleteMessage(t('directorConsole.ui.resultRemoved'));
    } catch (error) {
      setDeleteMessage(error instanceof Error ? error.message : t('directorConsole.ui.deleteUnavailable'));
    }
  }, [jobId, url]);
  const editHref = `/chopshop?edit=${encodeURIComponent(url)}`;
  return (
    <div style={{ background: P.card, border: `1px solid rgba(52,211,153,.3)`, borderRadius: 12, padding: 14 }}>
      <div style={{ fontSize: 11, letterSpacing: 2.5, color: P.green, fontWeight: 700, marginBottom: 10, fontFamily: "'Rajdhani', sans-serif" }}>{t('directorConsole.ui.resultReady')}</div>
      {isVideo ? <video src={url} controls style={{ width: "100%", borderRadius: 8, maxHeight: 260, marginBottom: 10 }} /> : <img src={url} alt={t('directorConsole.ui.resultAlt')} style={{ width: "100%", borderRadius: 8, maxHeight: 260, objectFit: "cover", marginBottom: 10 }} />}
      <div style={{ marginBottom: 12, background: "rgba(255,255,255,0.04)", border: `1px solid ${P.border}`, borderRadius: 10, padding: 12 }}>
        <div style={{ fontSize: 13, color: P.offwhite, fontWeight: 700, marginBottom: 8 }}>{t('directorConsole.ui.requiredReview')}
        </div>
        <div style={{ fontSize: 13, color: P.muted, lineHeight: 1.5, marginBottom: 10 }}>{t('directorConsole.ui.reviewExplain')}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button" onClick={() => setRating(value)}
              style={{
                border: `1px solid ${rating === value ? P.cyan : P.border}`,
                background: rating === value ? "rgba(56,189,248,.14)" : "transparent",
                color: rating === value ? P.offwhite : P.muted,
                borderRadius: 8,
                padding: "7px 10px",
                cursor: "pointer",
                fontSize: 13,
                fontFamily: "'Rajdhani', sans-serif",
              }}
            >
              {value}/5
            </button>
          ))}
        </div>
        <textarea
          value={review}
          onChange={(event) => setReview(event.target.value)}
          placeholder={t('directorConsole.ui.feedbackPlaceholder')} style={{
            width: "100%",
            minHeight: 88,
            resize: "vertical",
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${P.border}`,
            color: P.offwhite,
            borderRadius: 8,
            padding: 10,
            fontSize: 12,
            marginBottom: 10,
          }}
        />
        <button
          type="button" disabled={!canSubmit}
          onClick={async () => {
            try {
              setSubmitting(true);
              setErrorMessage(null);
              const response = await submitDirectorFeedback({
                baseUrl,
                routePrefix,
                authToken,
                conversationId,
                jobId,
                sessionId,
                rating,
                review,
              });
              setSubmittedMessage(response.learning_note || response.message);
            } catch (error) {
              setErrorMessage(error instanceof Error ? error.message : t('directorConsole.ui.feedbackFailed'));
            } finally {
              setSubmitting(false);
            }
          }}
          style={{
            width: "100%",
            border: `1px solid ${canSubmit ? P.cyan : P.border}`,
            background: canSubmit ? `linear-gradient(135deg,${P.cyanDim},rgba(191,64,255,.16))` : "transparent",
            color: canSubmit ? P.offwhite : P.muted,
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 12,
            fontWeight: 700,
            cursor: canSubmit ? "pointer" : "not-allowed",
            marginBottom: 10,
          }}
        >
          {submitting ? t('directorConsole.ui.savingReview') : submittedMessage ? t('directorConsole.ui.reviewSaved') : t('directorConsole.ui.submitReview')}
        </button>
        {submittedMessage ? <div style={{ fontSize: 13, color: P.green, lineHeight: 1.5 }}>{submittedMessage}</div> : null}
        {errorMessage ? <div style={{ fontSize: 13, color: P.red, lineHeight: 1.5 }}>{errorMessage}</div> : null}
      </div>
      <a href={url} target="_blank" rel="noreferrer" style={{ display: "block", textAlign: "center", textDecoration: "none", background: `linear-gradient(135deg,${P.cyan},${P.purple})`, color: P.bg, borderRadius: 8, padding: "9px 16px", fontSize: 12, fontWeight: 700, letterSpacing: 1.5, fontFamily: "'Rajdhani', sans-serif", opacity: submittedMessage ? 1 : 0.55, pointerEvents: submittedMessage ? "auto" : "none" }}>{t('directorConsole.ui.openResult')}
      </a>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(104px, 1fr))", gap: 8, marginTop: 10 }}>
        <button type="button" onClick={doDownload} style={{ borderRadius: 8, border: `1px solid ${P.border}`, background: "transparent", color: P.offwhite, padding: "8px 10px", fontSize: 13, cursor: "pointer" }}>{t('directorConsole.ui.download')}</button>
        <button type="button" onClick={() => void doCopy()} style={{ borderRadius: 8, border: `1px solid ${P.border}`, background: "transparent", color: P.offwhite, padding: "8px 10px", fontSize: 13, cursor: "pointer" }}>{t('directorConsole.ui.saveLink')}</button>
        <button type="button" onClick={() => void doShare()} style={{ borderRadius: 8, border: `1px solid ${P.border}`, background: "transparent", color: P.offwhite, padding: "8px 10px", fontSize: 13, cursor: "pointer" }}>{t('directorConsole.ui.share')}</button>
        <a href={editHref} style={{ textAlign: "center", textDecoration: "none", borderRadius: 8, border: `1px solid ${P.border}`, color: P.offwhite, padding: "8px 10px", fontSize: 13 }}>{t('directorConsole.ui.edit')}</a>
        <a href="/chopshop" data-testid="landing-director-open-in-chopshop" style={{ textAlign: "center", textDecoration: "none", borderRadius: 8, border: `1px solid ${P.border}`, color: P.offwhite, padding: "8px 10px", fontSize: 13 }}>{t('directorConsole.ui.openInChopShop')}</a>
        <button type="button" onClick={() => void doDelete()} style={{ borderRadius: 8, border: `1px solid ${P.pink}`, background: "transparent", color: P.pink, padding: "8px 10px", fontSize: 13, cursor: "pointer" }}>{t('directorConsole.ui.delete')}</button>
      </div>
      {deleteMessage ? <div style={{ fontSize: 13, color: P.muted, marginTop: 8 }}>{deleteMessage}</div> : null}
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const { t } = useTranslation();
  const map: Record<string, { color: string; text: string }> = {
    idle: { color: P.dim, text: t('directorConsole.ui.statusOffline') },
    screening: { color: P.cyan, text: t('directorConsole.ui.statusScreening') },
    ready: { color: P.green, text: t('directorConsole.ui.statusReady') },
    processing: { color: P.gold, text: t('directorConsole.ui.statusProcessing') },
    done: { color: P.green, text: t('directorConsole.ui.statusDone') },
    error: { color: P.red, text: t('directorConsole.ui.statusError') },
  };
  const active = map[status] ?? map.idle;
  return (
    <div className="ldc-status" style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 7, height: 7, borderRadius: "50%", background: active.color, boxShadow: `0 0 8px ${active.color}`, animation: status === "processing" ? "ldc-shimmer 1.2s ease-in-out infinite" : undefined }} />
      <span style={{ fontSize: 11, letterSpacing: 3, fontWeight: 700, color: active.color, fontFamily: "'Rajdhani', sans-serif" }}>{active.text}</span>
    </div>
  );
}

// ViralBoostPanel removed — the Viral Algorithm Boost feature is retired.

// Inline SVG flags — Windows renders flag EMOJIS as plain letters ("US"/"MX"),
// so the bilingual toggle draws real flags instead.
// The Director is bilingual — this obvious flag switch flips the whole console
// (and the chat backend already receives the language with every message, so
// the Director agent answers in the selected language too).
function LangToggle() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.toLowerCase().startsWith("es") ? "es" : "en";
  const buttonStyle = (active: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 8px",
    borderRadius: 8,
    cursor: "pointer",
    border: `1px solid ${active ? P.cyan : P.border}`,
    background: active ? "rgba(56,189,248,.12)" : "transparent",
    opacity: active ? 1 : 0.55,
    transition: "all .15s ease",
  });
  return (
    // ENG / ESP and translate="no", matching the top-bar toggle: browser page
    // translation read "EN"/"ES" as the Spanish words for "in"/"is" and showed
    // "IN"/"IS". See components/ui/LanguageFlagToggle.tsx.
    <div
      translate="no"
      className="notranslate"
      style={{ display: "flex", gap: 4, flexShrink: 0 }}
      role="group"
      aria-label={t('directorConsole.ui.directorLanguage')}
    >
      <button
        type="button" data-testid="landing-director-lang-en" aria-pressed={lang === "en"}
        title={t('directorConsole.ui.speaksEnglish')} onClick={() => void i18n.changeLanguage("en")}
        style={buttonStyle(lang === "en")}
      >
        <FlagUS />
        <span className="ldc-btn-label" style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: lang === "en" ? P.cyan : P.muted, fontFamily: "'Rajdhani', sans-serif" }}>ENG</span>
      </button>
      <button
        type="button" data-testid="landing-director-lang-es" aria-pressed={lang === "es"}
        title={t('directorConsole.ui.speaksSpanish')} onClick={() => void i18n.changeLanguage("es")}
        style={buttonStyle(lang === "es")}
      >
        <FlagMX />
        <span className="ldc-btn-label" style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: lang === "es" ? P.cyan : P.muted, fontFamily: "'Rajdhani', sans-serif" }}>ESP</span>
      </button>
    </div>
  );
}

function extractResultUrl(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const candidates = [record, record.raw as Record<string, unknown>, record.data as Record<string, unknown>].filter(Boolean) as Array<Record<string, unknown>>;
  for (const candidate of candidates) {
    const url = candidate.result_url ?? candidate.resultUrl ?? candidate.output_url ?? candidate.url ?? (Array.isArray(candidate.urls) ? candidate.urls[0] : null);
    if (typeof url === "string" && url.startsWith("http")) return url;
  }
  return null;
}

function extractStatusMessage(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  if (typeof record.message === "string" && record.message.trim()) return record.message;
  if (typeof record.status === "string" && record.status.trim()) return record.status;
  return null;
}

function extractPlannedState(raw: unknown): { planned: boolean; toolCount: number } {
  if (!raw || typeof raw !== "object") return { planned: false, toolCount: 0 };
  const record = raw as Record<string, unknown>;
  const toolCalls = Array.isArray(record.tool_calls) ? record.tool_calls : [];
  return { planned: record.planned === true, toolCount: toolCalls.length };
}

function extractDirectorPlan(raw: unknown): DirectorPlan | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const candidate = record.director_plan;
  if (!candidate || typeof candidate !== "object") return null;
  const plan = candidate as DirectorPlan;
  if (typeof plan.plan_id !== "string" || !plan.plan_id.trim()) return null;
  if (!Array.isArray(plan.steps)) return null;
  if (!plan.totals || typeof plan.totals !== "object") return null;
  return plan;
}

function formatPlanCost(t: TFunction, credits: number, amountCents: number, currency: string): string {
  const safeCredits = Number.isFinite(credits) ? Math.max(0, credits) : 0;
  const safeAmount = Number.isFinite(amountCents) ? Math.max(0, amountCents) : 0;
  const safeCurrency = typeof currency === "string" && currency.trim() ? currency.trim().toUpperCase() : "MXN";
  return t('directorConsole.ui.planCost', { credits: safeCredits, amount: (safeAmount / 100).toFixed(2), currency: safeCurrency });
}

function extractPrimaryJobId(result: ChatStartResult | PollResult): string | null {
  if (Array.isArray(result.job_ids) && typeof result.job_ids[0] === "string" && result.job_ids[0].trim()) {
    return result.job_ids[0].trim();
  }
  if ("workflow_run_id" in result && typeof result.workflow_run_id === "string" && result.workflow_run_id.trim()) {
    return result.workflow_run_id.trim();
  }
  const raw = "raw" in result ? result.raw : undefined;
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  if (typeof record.id === "string" && record.id.trim()) return record.id.trim();
  if (Array.isArray(record.job_ids) && typeof record.job_ids[0] === "string" && record.job_ids[0].trim()) {
    return record.job_ids[0].trim();
  }
  return null;
}

function extractProgressSnapshot(t: TFunction, raw: unknown, tick: number): { label: string; progress: number; stepIndex: number; totalSteps: number } {
  const record = raw && typeof raw === "object" ? raw as Record<string, unknown> : {};
  const nested = (record.raw && typeof record.raw === "object" ? record.raw as Record<string, unknown> : null)
    || (record.data && typeof record.data === "object" ? record.data as Record<string, unknown> : null);
  const source = nested || record;
  const statusCandidate = String(source.current_status ?? source.status ?? "").trim().toLowerCase();
  const explicitProgress = Number(source.progress ?? source.percent ?? source.percentage);
  if (Number.isFinite(explicitProgress) && explicitProgress >= 0 && explicitProgress <= 100) {
    const safe = Math.round(Math.max(1, Math.min(99, explicitProgress)));
    const message = extractStatusMessage(raw) || t('directorConsole.ui.directorWorking');
    const stepIndex = safe >= 80 ? 3 : safe >= 40 ? 2 : 1;
    return { label: message, progress: safe, stepIndex, totalSteps: 4 };
  }
  if (statusCandidate === "queued" || statusCandidate === "pending" || statusCandidate === "created") {
    return { label: t('directorConsole.ui.queuedWaiting'), progress: 10, stepIndex: 1, totalSteps: 4 };
  }
  if (statusCandidate === "finalizing" || statusCandidate === "saving" || statusCandidate === "uploading") {
    return { label: t('directorConsole.ui.finalizingOutput'), progress: 80, stepIndex: 3, totalSteps: 4 };
  }
  const fallback = Math.min(30 + tick * 2, 92);
  const runningLabel = extractStatusMessage(raw) || t('directorConsole.ui.directorWorking');
  return { label: runningLabel, progress: Math.round(fallback), stepIndex: 2, totalSteps: 4 };
}

type ChatStartResult = Awaited<ReturnType<typeof sendChatMessage>>;
type PollResult = Awaited<ReturnType<typeof pollChatStatus>>;
type PendingLaunchPlan = {
  plan: DirectorPlan;
  conversationId: string | null;
  authToken?: string;
  sessionId: string;
};

function getStoredUserId() {
  if (typeof window === "undefined") return "";
  const stored = window.localStorage.getItem(USER_STORAGE_KEY);
  if (stored) return stored;
  const next = `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.localStorage.setItem(USER_STORAGE_KEY, next);
  return next;
}

// `visible` is false while the landing page shows a stop with no console
// (welcome, storybook): HeroSection keeps the console mounted but hidden then,
// so a render in flight keeps polling. Standalone uses leave it on.
export default function DirectorConsole({ tool, visible = true }: { tool?: ConsoleToolId; visible?: boolean }) {
  const { t, i18n } = useTranslation();
  // The story studio (2026-10-03): until the server can reach it, the workflow says "coming soon" and starts nothing.
  const storyAvailable = useStoryAvailability();
  const storyAvailableRef = useRef(storyAvailable);
  storyAvailableRef.current = storyAvailable;
  // True while the chat's last word on the story is "coming soon" (said from the default before
  // /api/web/story/config answered): the intro runs again when the answer is yes (review, 2026-10-03).
  const storySoonSaidRef = useRef(false);
  const { isAuthenticated, user } = useAuth();
  const backendBase = resolveBackendBase((import.meta as unknown as { env?: Record<string, unknown> }).env);
  const routePrefix = DIRECTOR_ROUTE_PREFIX;

  const subTypes = getTranslatedSubTypes(t);

  const [bootstrapped, setBootstrapped] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [typing, setTyping] = useState(false);
  const [status, setStatus] = useState("idle");
  const [phase, setPhase] = useState<"screening" | "chat">("screening");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [userId, setUserId] = useState("");
  const [outputType, setOutputType] = useState<OutputType | null>(null);
  const [subType, setSubType] = useState<SubType | null>(null);
  // MAXIMIZED: once a workflow (corrido / TikTok story) is engaged, the console
  // takes over the whole viewport so the board + chat are the only thing on
  // screen — no landing-page scroll, no second scrollbar (user directive
  // 2026-07-21). A Home button exits back to the landing page.
  const [maximized, setMaximized] = useState(false);
  const [assets, setAssets] = useState<Record<string, string | File>>({});
  const [assetDisplayLabels, setAssetDisplayLabels] = useState<Record<string, string>>({});
  const [description, setDescription] = useState("");
  const [videoDuration, setVideoDuration] = useState<VideoDurationChoice | null>(null);
  const [aspectRatio, setAspectRatio] = useState<AspectRatioChoice | null>(null);
  const [resumeDraft, setResumeDraft] = useState<StoredDirectorDraft | null>(null);
  const [shouldPersistDraft, setShouldPersistDraft] = useState(true);
  const [chatInput, setChatInput] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [bgMediaUrl, setBgMediaUrl] = useState<string>("");
  const bgUploadInputRef = useRef<HTMLInputElement | null>(null);
  // Viral Algorithm Boost retired — the panel/toggle is gone from the console.
  // The constant keeps the media-agent request payload shape unchanged.
  const viralBoost = false;
  // The storyboard BOARD is ALWAYS on screen — it is not a mode, a panel, or a
  // thing you go and open. Approving a storyboard is a main component of every
  // tool we ship, so the canvas is the workspace and the chat sits beside it
  // (the CapCut arrangement). It used to hide behind a "BOARD ▸" toggle, which
  // made the approval surface something users had to discover.
  const studioProject = useStoryboardProject();
  const storyBrief = useStoryBrief();

  // Is a chat widget in the middle of a job? CorridoChatFlow and StoryChatFlow
  // run their own renders, which jobRunningRef never sees, but they report
  // every one on the studio bus: a progress card while a job runs (corrido
  // build / animate / assemble / revise, story plan / render), the clip
  // review's `rerolling` while one corrido clip re-renders, and a
  // "regenerating" clip while one still is redone. Each flow clears these when
  // the job ends and again when the widget unmounts, so together they are the
  // widgets' busy flag. Read through a ref so effects don't re-run on every
  // progress tick.
  const studioProgress = useStudioProgress();
  const clipReview = useClipReview();
  // Which flows are busy, not just whether one is: a workflow start is held
  // for a different flow but is a no-op for the busy one (holdStartWhileBusy).
  const busyServicesRef = useRef<StudioService[]>([]);
  const widgetBusyRef = useRef(false);
  const busyServices: StudioService[] = studioProgress.map((entry) => entry.service);
  if (typeof clipReview?.rerolling === "number") busyServices.push("corrido"); // clip review is corrido's
  if (studioProject?.clips?.some((clip) => clip.status === "regenerating")) busyServices.push(studioProject.service);
  busyServicesRef.current = busyServices;
  widgetBusyRef.current = busyServices.length > 0;
  // The subtype whose flow is in the chat, for the console's own jobs (a
  // reconnected run), which the studio bus doesn't carry.
  const subTypeIdRef = useRef<string | null>(null);
  subTypeIdRef.current = subType?.id ?? null;

  // The tool whose conversation is on screen. Normally the compass `tool`, but
  // a compass switch made while a render is running is held back (see COMPASS
  // SWITCH below), and the board has to stay with the chat meanwhile: the
  // story approval card and the corrido progress node only show when the
  // board's activeService matches the flow that is actually running.
  const [shownTool, setShownTool] = useState(tool);
  const shownToolRef = useRef(tool);
  const showTool = useCallback((next: typeof tool) => {
    shownToolRef.current = next;
    setShownTool(next);
  }, []);

  // Chat panel width — the user drags the seam between board and chat. Kept in
  // px and remembered, because which side you want bigger depends on the job:
  // reviewing a storyboard wants canvas, arguing with the Director wants chat.
  const workspaceRef = useRef<HTMLDivElement>(null);
  const [chatWidth, setChatWidth] = useState<number>(() => {
    const saved = Number(typeof window !== "undefined" ? window.localStorage.getItem(CHAT_WIDTH_KEY) : NaN);
    return Number.isFinite(saved) && saved >= CHAT_MIN ? saved : 420;
  });
  const [resizing, setResizing] = useState(false);

  const onResizeStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const host = workspaceRef.current;
    if (!host) return;
    setResizing(true);
    const move = (e: PointerEvent) => {
      const rect = host.getBoundingClientRect();
      // Width measured from the RIGHT edge — the chat is the right column.
      const next = Math.round(rect.right - e.clientX);
      // Never let either side collapse to nothing.
      const max = Math.max(CHAT_MIN, rect.width - CANVAS_MIN);
      setChatWidth(Math.min(max, Math.max(CHAT_MIN, next)));
    };
    const up = () => {
      setResizing(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setChatWidth((w) => {
        try { window.localStorage.setItem(CHAT_WIDTH_KEY, String(w)); } catch { /* quota */ }
        return w;
      });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, []);
  // The tool rail starts a workflow through the SAME handler the greeting cards
  // use — one code path, so the rail can never drift from the cards.
  //
  // Starting a workflow clears the chat's widgets, so while a render is
  // running it is held like a compass switch (see holdStartWhileBusy): the
  // "Make my music video" / TikTok CTAs in the pitch sit right above a busy
  // console, and after a held switch they name the OTHER tool. The CTA still
  // opens the studio fullscreen, where the running job (or the Director's
  // note about it) is. (2026-09-24)
  useWorkflowStartEvent(
    useCallback((id: WorkflowId) => {
      if (holdStartWhileBusyRef.current(workflowService(id)) !== null) {
        setMaximized(true);
        return;
      }
      const sub = getTranslatedSubTypes(t).video.find((s) => s.id === id);
      if (sub) void handleSubTypeRef.current("video", sub);
    }, [t]),
  );
  const [currentStep, setCurrentStep] = useState<"output" | "subtype" | "asset" | "description" | "confirm" | "chat">("output");
  const [currentAssetKey, setCurrentAssetKey] = useState<AssetKey | null>(null);
  const [currentAssetOptional, setCurrentAssetOptional] = useState(false);
  const [pendingLaunchPlan, setPendingLaunchPlan] = useState<PendingLaunchPlan | null>(null);
  const [launchInProgress, setLaunchInProgress] = useState(false);
  const [launchStateText, setLaunchStateText] = useState<string | null>(null);

  const { balance: creditBalance } = useUserCredits(isAuthenticated);

  // The chat's own scroll box (the overflowY:auto message list).
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const jobRunningRef = useRef(false);
  const activePollRunIdRef = useRef<string | null>(null);
  const pollCancelledRef = useRef(false);
  const introQueuedRef = useRef(false);
  const handleOutputTypeRef = useRef<(type: OutputType) => Promise<void> | void>(() => { });
  // Lets the intro greeting (and any other stale-closure spot) launch a
  // workflow subtype directly — same pattern as handleOutputTypeRef.
  const handleSubTypeRef = useRef<(type: OutputType, sub: SubType) => Promise<void> | void>(() => { });
  // runIntro is defined below handleRestart; the ref lets restart replay it.
  const runIntroRef = useRef<() => Promise<void>>(async () => { });
  const persistConversationId = useCallback((nextConversationId: string | null) => {
    setConversationId(nextConversationId);
    if (typeof window === "undefined") return;
    if (nextConversationId) {
      window.localStorage.setItem(CONVERSATION_STORAGE_KEY, nextConversationId);
      return;
    }
    window.localStorage.removeItem(CONVERSATION_STORAGE_KEY);
  }, []);

  // Scroll the chat list, never the page. This used scrollIntoView on a
  // sentinel under the last message, which also scrolls every scrollable
  // ancestor, window included; since the intro replays on first load and on
  // every compass switch, the landing page glided down to the console on its
  // own a moment after each one (2026-09-24). Scrolling the list itself can't
  // move anything outside it.
  //
  // NEWEST MESSAGE FROM ITS FIRST LINE (2026-09-25). Scrolled to the very
  // bottom, a message taller than the log opened mid-sentence: on a 320px
  // phone the Director's greeting started at "rendered end-to-end by our
  // in-house engine." with its hook above the log's top edge. The log now
  // stops where the newest message starts, so a long one reads from its
  // first line and the rest scrolls up under the thumb; anything that fits
  // lands exactly where it did. (offsetParent is null in jsdom, which keeps
  // the plain bottom scroll there.)
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      const list = chatScrollRef.current;
      if (!list) return;
      let top = list.scrollHeight;
      const newest = list.lastElementChild as HTMLElement | null;
      if (newest && newest.offsetParent === list) {
        // Stop where the message above it ends, so no sliver of that bubble's
        // edge shows over the newest one; the first message stops at 0.
        const above = newest.previousElementSibling as HTMLElement | null;
        top = Math.min(top, above ? above.offsetTop + above.offsetHeight : 0);
      }
      try {
        list.scrollTo({ top, behavior: "smooth" });
      } catch {
        // No Element.scrollTo, or no options-object support: jump instead.
        list.scrollTop = top;
      }
    }, 80);
  }, []);

  const persistDraft = useCallback((nextDraft: StoredDirectorDraft | null) => {
    if (typeof window === "undefined") return;
    if (!nextDraft) {
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(nextDraft));
  }, []);

  // `unless`: asked when the typing delay ends; true drops the line (the story's
  // "coming soon" when the server said yes meanwhile; review, 2026-10-03).
  const dirSay = useCallback((text: string, widget: ReactNode = null, wide = false, delay = 500, unless?: () => boolean) => {
    return new Promise<void>((resolve) => {
      setTyping(true);
      window.setTimeout(() => {
        setTyping(false);
        if (unless && unless()) { resolve(); return; }
        setMsgs((current) => [...current, { id: uid(), from: "director", text, widget, wide }]);
        scrollToBottom();
        resolve();
      }, delay);
    });
  }, [scrollToBottom]);

  // A workflow start (pitch CTA, tool rail, or a chat keyword) while a render
  // is running. handleSubType clears the chat's widgets, which unmounts a
  // flow that is still polling its job, and the story flow keeps no reconnect
  // record, so a paid render would drop off the screen. Returns null when
  // nothing is busy (go ahead), "same" when the busy flow IS the one asked
  // for (leave it mounted: it is already there), and "other" when a different
  // flow is busy, after the Director says to start the new one afterwards.
  const holdStartWhileBusy = useCallback((wanted: StudioService): "same" | "other" | null => {
    const busy = [...busyServicesRef.current];
    if (jobRunningRef.current) busy.push(workflowService(subTypeIdRef.current));
    if (busy.length === 0) return null;
    if (busy.includes(wanted)) return "same";
    void dirSay(
      t(busy[0] === "story" ? "directorConsole.ui.storyStillRendering"
        : busy[0] === "ads" ? "directorConsole.ui.adsStillRendering"
        : "directorConsole.ui.corridoStillRendering"),
      null, false, 200,
    );
    return "other";
  }, [dirSay, t]);
  const holdStartWhileBusyRef = useRef(holdStartWhileBusy);
  holdStartWhileBusyRef.current = holdStartWhileBusy;

  const userSay = useCallback((text: string) => {
    setMsgs((current) => [...current, { id: uid(), from: "user", text }]);
    scrollToBottom();
  }, [scrollToBottom]);

  const clearWidgets = useCallback(() => {
    setMsgs((current) => current.map((msg) => ({ ...msg, widget: null })));
  }, []);

  const updateMsg = useCallback((id: number, patch: Partial<Msg>) => {
    setMsgs((current) => current.map((msg) => (msg.id === id ? { ...msg, ...patch } : msg)));
  }, []);

  const resetDraft = useCallback(() => {
    persistConversationId(null);
    setOutputType(null);
    setSubType(null);
    setAssets({});
    setAssetDisplayLabels({});
    setDescription("");
    setVideoDuration(null);
    setAspectRatio(null);
    setResumeDraft(null);
    setShouldPersistDraft(true);
    setPhase("screening");
    setStatus("screening");
    setCurrentStep("output");
    setCurrentAssetKey(null);
    setCurrentAssetOptional(false);
    setPendingLaunchPlan(null);
    setLaunchInProgress(false);
    setLaunchStateText(null);
    persistDraft(null);
  }, [persistConversationId, persistDraft]);


  const handleChatSend = async () => {
    const value = chatInput.trim();
    if (!value || sendingChat || jobRunningRef.current) return;
    setSendingChat(true);
    userSay(value);
    setChatInput("");
    // Corrido is chat-triggerable: any prompt asking for a corrido / music video
    // drops the user straight into the in-console studio (the ONLY place the
    // local-ComfyUI corrido pipeline can be driven — there is no separate page).
    // Either keyword route below is held while a render is running (see
    // holdStartWhileBusy); asking for the flow that is already busy just
    // falls through to the ordinary chat reply.
    const corridoHold = subType?.id !== "corrido" && /\bcorridos?\b|music\s*video/i.test(value) ? holdStartWhileBusy("corrido") : null;
    if (corridoHold === "other") {
      setSendingChat(false);
      return;
    }
    if (corridoHold === null && subType?.id !== "corrido" && /\bcorridos?\b|music\s*video/i.test(value)) {
      const corridoSub = subTypes.video.find((sub) => sub.id === "corrido");
      if (corridoSub) {
        setOutputType("video");
        setSendingChat(false);
        await handleSubType("video", corridoSub);
        return;
      }
    }
    // Kids' picture books have their own studio at /storybook. They have to be
    // caught before the TikTok check below, where \bstory\b sent "a story book
    // for my kid" into the TikTok flow and fullscreen. Point them at the
    // storybook page instead: no maximize, nothing started. (2026-09-24)
    if (isKidsStorybookRequest(value)) {
      await dirSay(t('directorConsole.ui.storybookRedirect'), <StorybookLink />, false, 200);
      setSendingChat(false);
      return;
    }
    // Promo / commercial / ad cues point at the Commercials & Ads tool and start
    // nothing else: the retired A2E promo pipeline never dispatches again.
    // Only when NO workflow is active, or when the message is a short, explicit
    // tool request ("make an ad", "quiero un anuncio"): mid-flow edits that
    // merely use the word ("make the chorus sound more commercial", "un corrido
    // para la promo de mi tienda") stay in the flow the customer is in. Held
    // while a render is running, like the corrido and story routes.
    const wantsAds = subType?.id !== "ads" && isAdsRequest(value) && (!subType || isShortAdsToolRequest(value));
    const adsHold = wantsAds ? holdStartWhileBusy("ads") : null;
    if (adsHold === "other") {
      setSendingChat(false);
      return;
    }
    if (wantsAds && adsHold === null) {
      const adsSub = subTypes.video.find((sub) => sub.id === "ads");
      if (adsSub) {
        setOutputType("video");
        setSendingChat(false);
        await dirSay(t('directorConsole.ui.useAdsTool'), null, false, 200);
        await handleSubType("video", adsSub);
        return;
      }
    }
    // TikTok story prompts open the in-console story flow the same way.
    // (\bstory\b does not match "storyboard", so board edits stay unaffected.)
    const storyHold = subType?.id !== "tiktok_story" && /\btik\s*tok\b|\bstory\b/i.test(value) ? holdStartWhileBusy("story") : null;
    if (storyHold === "other") {
      setSendingChat(false);
      return;
    }
    if (storyHold === null && subType?.id !== "tiktok_story" && /\btik\s*tok\b|\bstory\b/i.test(value)) {
      const storySub = subTypes.video.find((sub) => sub.id === "tiktok_story");
      if (storySub) {
        setOutputType("video");
        setSendingChat(false);
        await handleSubType("video", storySub);
        return;
      }
    }
    if (currentStep !== "chat") {
      const reply = buildOffScriptReply({
        mode: "landing",
        step: currentStep,
        outputTypeLabel: outputType ? outputType.charAt(0).toUpperCase() + outputType.slice(1) : null,
        subtypeLabel: subType?.label ?? null,
        assetLabel: currentAssetKey ? assetLabel(t, currentAssetKey) : null,
        assetOptional: currentAssetOptional,
        needsVideoSpecs: outputType === "video" ? { duration: !videoDuration, aspect: !aspectRatio } : null,
      });

      // Re-attach the relevant widget so the user can actually proceed
      // (the previous widget may have scrolled out of view)
      let widget: ReactNode = null;
      let wide = false;
      if (currentStep === "output") {
        widget = <OutputPicker onSelect={(type) => void handleOutputTypeRef.current(type)} />;
        wide = true;
      } else if (currentStep === "subtype" && outputType) {
        widget = <SubTypePicker subs={subTypes[outputType]} onSelect={(sub) => handleSubType(outputType, sub)} />;
        wide = true;
      } else if (currentStep === "asset" && outputType && subType && currentAssetKey) {
        const queue = [
          ...subType.assets.map((key) => ({ key, optional: false })),
          ...(subType.optional || []).map((key) => ({ key, optional: true })),
        ];
        const queueIndex = queue.findIndex((entry) => entry.key === currentAssetKey);
        const safeIndex = queueIndex >= 0 ? queueIndex : 0;
        widget = (
          <AssetInput
            assetKey={currentAssetKey}
            optional={currentAssetOptional}
            onSubmit={(payload) => handleAsset(outputType, subType, queue, safeIndex, currentAssetKey, payload, assets)}
            onSkip={currentAssetOptional ? () => handleAsset(outputType, subType, queue, safeIndex, currentAssetKey, "", assets) : undefined}
          />
        );
        wide = true;
      } else if (currentStep === "description" && outputType && subType) {
        const hint = describeHint(t, subType.id);
        widget = <DescribeInput hint={hint} onSubmit={(text) => handleDescription(outputType, subType, assets, text)} />;
        wide = true;
      }

      await dirSay(reply, widget, wide, 120);
      setSendingChat(false);
      return;
    }
    setPhase("chat");
    setStatus("chat");

    const authToken =
      typeof window !== "undefined" ? window.localStorage.getItem("auth_token") || undefined
        : undefined;

    try {
      const response = await sendChatMessage(value, {
        baseUrl: backendBase,
        routePrefix,
        conversationId,
        userId: userId || getStoredUserId(),
        authToken,
        viralBoost,
      });

      const nextConversationId = typeof response?.conversation_id === "string" && response.conversation_id.trim()
        ? response.conversation_id.trim()
        : conversationId;
      persistConversationId(nextConversationId ?? null);

      const reply = (typeof response?.message === "string" && response.message.trim())
        ? response.message.trim()
        : (extractStatusMessage(response?.raw) || t('directorConsole.ui.gotItTellMore'));
      await dirSay(reply, null, false, 200);
    } catch (error) {
      const rawMsg = error instanceof Error ? error.message : t('directorConsole.ui.somethingWentWrong');
      const message = rawMsg === "Failed to fetch" || rawMsg.includes("service_unavailable") || rawMsg.includes("tool_dispatch_timeout") || rawMsg.includes("operation_timeout")
        ? t('directorConsole.ui.serverSlow') : rawMsg;
      await dirSay(t('directorConsole.ui.somethingWentWrongDetail', { message }), null, false, 0);
    } finally {
      setSendingChat(false);
    }
  };

  const handleRestart = useCallback(async () => {
    // A corrido/story build in the chat is busy too (the studio bus): starting
    // over would unmount it mid-build, the same way the compass switch is held.
    if (jobRunningRef.current || widgetBusyRef.current) return;
    setMsgs([]);
    resetDraft();
    setMaximized(false); // starting over drops back to the (un-maximized) picker
    setCurrentStep("output");
    // Same greeting as first load: the TWO real workflows. The old restart
    // path showed the legacy A2E category picker (image/audio/utility) —
    // tools the engine can no longer run — so restarting stranded the user
    // in a menu of dead ends.
    await runIntroRef.current();
  }, [resetDraft]);

  async function askDescription(selectedOutputType: OutputType, selectedSubType: SubType, currentAssets: Record<string, string | File>) {
    const hint = describeHint(t, selectedSubType.id);
    setCurrentStep("description");
    setCurrentAssetKey(null);
    setCurrentAssetOptional(false);
    await dirSay(t('directorConsole.conversation.describeVision'), <DescribeInput hint={hint} onSubmit={(value) => handleDescription(selectedOutputType, selectedSubType, currentAssets, value)} />, true, 450);
  }

  async function askNextAsset(selectedOutputType: OutputType, selectedSubType: SubType, queue: Array<{ key: AssetKey; optional: boolean }>, index: number, currentAssets: Record<string, string | File>) {
    if (index >= queue.length) {
      await askDescription(selectedOutputType, selectedSubType, currentAssets);
      return;
    }

    const { key, optional } = queue[index];
    setCurrentStep("asset");
    setCurrentAssetKey(key);
    setCurrentAssetOptional(optional);
    const def = ASSET_DEFS[key];
    if (optional) {
      await dirSay(t('directorConsole.conversation.optionalPiece'), null, false, 250);
    }
    await dirSay(
      `${def.icon} ${t('directorConsole.conversation.needYour', { asset: def.label.toLowerCase() })}.`,
      <AssetInput
        assetKey={key}
        optional={optional}
        onSubmit={(payload) => handleAsset(selectedOutputType, selectedSubType, queue, index, key, payload, currentAssets)}
        onSkip={optional ? () => handleAsset(selectedOutputType, selectedSubType, queue, index, key, "", currentAssets) : undefined}
      />,
      true,
      450,
    );
  }

  async function handleAsset(
    selectedOutputType: OutputType,
    selectedSubType: SubType,
    queue: Array<{ key: AssetKey; optional: boolean }>,
    index: number,
    key: AssetKey,
    payload: AssetSubmitPayload | string,
    currentAssets: Record<string, string | File>
  ) {
    clearWidgets();
    const value = typeof payload === "string" ? payload : payload.value;
    const displayLabel = typeof payload === "string" ? undefined : payload.displayLabel;
    const nextAssets = value ? { ...currentAssets, [key]: (payload as { file?: File }).file || value } : currentAssets;
    const nextAssetDisplayLabels = value
      ? { ...assetDisplayLabels, [key]: displayLabel || fallbackAssetSelectionLabel(t, key, value) }
      : assetDisplayLabels;
    setAssets(nextAssets);
    setAssetDisplayLabels(nextAssetDisplayLabels);
    userSay(value ? `${ASSET_DEFS[key].icon} ${nextAssetDisplayLabels[key] || fallbackAssetSelectionLabel(t, key, value)}` : t('directorConsole.conversation.skipped'));
    if (resumeDraft && getMissingRequiredAssets(selectedSubType, nextAssets).length === 0 && description.trim()) {
      setResumeDraft({
        ...resumeDraft,
        assets: nextAssets,
      });
      setCurrentStep("confirm");
      setCurrentAssetKey(null);
      setCurrentAssetOptional(false);
      await dirSay(
        t('directorConsole.ui.faceLockedIn'),
        <ConfirmCard
          outputType={selectedOutputType}
          subType={selectedSubType}
          assets={nextAssets}
          assetDisplayLabels={nextAssetDisplayLabels}
          description={description}
          isAuthenticated={isAuthenticated}
          videoDuration={videoDuration}
          aspectRatio={aspectRatio}
          onDurationChange={setVideoDuration}
          onAspectRatioChange={setAspectRatio}
          onLaunch={(selectedDuration, selectedRatio) => handleLaunch(selectedOutputType, selectedSubType, nextAssets, description, selectedDuration, selectedRatio)}
          onAdjust={handleRestart}
        />,
        true,
        250,
      );
      return;
    }
    await askNextAsset(selectedOutputType, selectedSubType, queue, index + 1, nextAssets);
  }

  async function handleDescription(selectedOutputType: OutputType, selectedSubType: SubType, currentAssets: Record<string, string | File>, value: string) {
    clearWidgets();
    setShouldPersistDraft(true);
    setResumeDraft(null);
    setPendingLaunchPlan(null);
    setLaunchStateText(null);
    setDescription(value);
    setCurrentStep("confirm");
    setCurrentAssetKey(null);
    setCurrentAssetOptional(false);
    userSay(value.length > 60 ? `${value.slice(0, 58)}…` : value);
    await dirSay(
      t('directorConsole.heresYourBrief'),
      <ConfirmCard
        outputType={selectedOutputType}
        subType={selectedSubType}
        assets={currentAssets}
          assetDisplayLabels={assetDisplayLabels}
        description={value}
        isAuthenticated={isAuthenticated}
        videoDuration={videoDuration}
        aspectRatio={aspectRatio}
        onDurationChange={setVideoDuration}
        onAspectRatioChange={setAspectRatio}
        onLaunch={(selectedDuration, selectedRatio) => handleLaunch(selectedOutputType, selectedSubType, currentAssets, value, selectedDuration, selectedRatio)}
        onAdjust={handleRestart}
      />,
      true,
      500,
    );
  }

  async function handleSubType(type: OutputType, selectedSubType: SubType) {
    clearWidgets();
    userSay(`${selectedSubType.icon} ${selectedSubType.label}`);
    // Corrido music video: full local-pipeline flow lives in its own chat widget
    // (song + photos + intake validation + storyboard approval + render).
    if (selectedSubType.id === "corrido") {
      setSubType(selectedSubType);
      setStatus("ready");
      setMaximized(true); // take over the screen for the workspace
      await dirSay(t('directorConsole.workflow.corridoIntro'), <CorridoChatFlow />, true, 320);
      return;
    }
    // TikTok story: hook + scenes + transitions collected in-console, charged
    // via the story ledger, rendered on the local ComfyUI pipeline.
    if (selectedSubType.id === "tiktok_story") {
      if (!storyAvailableRef.current) {
        setStatus("idle");
        await dirSay(t('directorConsole.ui.storyComingSoon'), null, false, 200);
        return;
      }
      setSubType(selectedSubType);
      setStatus("ready");
      setMaximized(true); // take over the screen for the workspace
      await dirSay(t('directorConsole.workflow.storyIntro'), <StoryChatFlow />, true, 320);
      return;
    }
    // Commercials & Ads: brief + product photos + brand + voice collected
    // in-console; the plan is approved on the board; the ads worker renders it.
    if (selectedSubType.id === "ads") {
      setSubType(selectedSubType);
      setStatus("ready");
      setMaximized(true); // take over the screen for the workspace
      await dirSay(t('directorConsole.workflow.adsIntro'), <AdsChatFlow />, true, 320);
      return;
    }
    setSubType(selectedSubType);
    setAssets({});
    setAssetDisplayLabels({});
    setPendingLaunchPlan(null);
    setLaunchStateText(null);
    setVideoDuration(null);
    setAspectRatio(null);
    setStatus("ready");

    const queue = [
      ...selectedSubType.assets.map((key) => ({ key, optional: false })),
      ...(selectedSubType.optional || []).map((key) => ({ key, optional: true })),
    ];

    await dirSay(selectedSubType.desc, null, false, 320);

    if (queue.length > 0) {
      await askNextAsset(type, selectedSubType, queue, 0, {});
      return;
    }

    await askDescription(type, selectedSubType, {});
  }

  async function handleOutputType(type: OutputType) {
    clearWidgets();
    userSay({ video: t('directorConsole.ui.saidVideo') }[type]);
    setOutputType(type);
    setPendingLaunchPlan(null);
    setLaunchStateText(null);
    setVideoDuration(null);
    setAspectRatio(null);
    setCurrentStep("subtype");
    await dirSay(t('directorConsole.ui.whatKind', { type: t(`directorConsole.ui.said${type.charAt(0).toUpperCase()}${type.slice(1)}`) }), <SubTypePicker subs={subTypes[type]} onSelect={(sub) => handleSubType(type, sub)} />, true, 420);
  }
  handleOutputTypeRef.current = handleOutputType;
  handleSubTypeRef.current = handleSubType;

  const pollRun = useCallback(async (runId: string, token?: string, progressId?: number) => {
    if (activePollRunIdRef.current === runId) {
      return { status: "failed", error: "Polling is already active for this job.", job_ids: [runId] } as PollResult;
    }
    activePollRunIdRef.current = runId;
    let tick = 0;
    try {
      const result = await pollChatStatus(runId, {
        baseUrl: backendBase,
        routePrefix,
        authToken: token,
        viralBoost,
        intervalMs: 3000,
        maxTicks: 200,
        shouldStop: () => pollCancelledRef.current,
        onTick: (raw) => {
          if (pollCancelledRef.current) return;
          tick += 1;
          const snapshot = extractProgressSnapshot(t, raw, tick);
          if (typeof progressId === "number") {
            updateMsg(progressId, {
              widget: (
                <ProgressWidget
                  label={snapshot.label}
                  progress={snapshot.progress}
                  detail={t('directorConsole.ui.processingStep', { step: snapshot.stepIndex, total: snapshot.totalSteps })}
                />
              ),
              wide: true
            });
          }
        },
      });
      return result;
    } finally {
      if (activePollRunIdRef.current === runId) {
        activePollRunIdRef.current = null;
      }
    }
  }, [backendBase, routePrefix, updateMsg, viralBoost]);

  const finalizeRun = useCallback(async (
    result: ChatStartResult | PollResult,
    progressId: number,
    meta?: { conversationId?: string | null; jobId?: string | null; authToken?: string; sessionId?: string }
  ) => {
    // Whatever the outcome (result, failure, or "still processing in the
    // background"), the run is no longer something to auto-reconnect to on the
    // next reload, so drop the persisted resume marker here.
    clearActiveRun();
    const raw = 'raw' in result ? result.raw : undefined;
    const resultUrl = extractResultUrl(raw);
    const statusMessage = result.status === "failed" ? result.error : result.message;
    const pollTimeoutMessage =
      result.status === "failed" && (
        statusMessage === "Timeout" ||
        ('details' in result && typeof (result as { details?: { message?: unknown } }).details?.message === "string"
          && /timed out/i.test(String((result as { details?: { message?: unknown } }).details?.message)))
      );
    if (resultUrl) {
      updateMsg(progressId, {
        widget: (
          <ResultWidget
            url={resultUrl}
            conversationId={meta?.conversationId ?? conversationId ?? null}
            jobId={meta?.jobId ?? extractPrimaryJobId(result)}
            sessionId={meta?.sessionId ?? (userId || getStoredUserId())}
            authToken={meta?.authToken}
            baseUrl={backendBase}
            routePrefix={routePrefix}
          />
        ),
        wide: true
      });
      await dirSay(statusMessage && statusMessage.trim() ? statusMessage : t('directorConsole.ui.thatsAWrap'), null, false, 260);
      setStatus("done");
      return;
    }
    if (result.status === "failed") {
      if (pollTimeoutMessage) {
        updateMsg(progressId, { widget: <CompletedAwaitingMediaWidget />, wide: true });
        await dirSay(t('directorConsole.ui.stillProcessingBackground'), null, false, 200);
        setStatus("done");
        return;
      }
      const failedMessage = statusMessage && statusMessage.trim() ? statusMessage : t('directorConsole.ui.jobFailedNoMedia');
      updateMsg(progressId, { widget: <FailedWidget message={failedMessage} onRetry={() => void handleRestart()} />, wide: true });
      await dirSay(t('directorConsole.ui.jobFailedDetail', { message: failedMessage }), null, false, 200);
      setStatus("error");
      return;
    }
    updateMsg(progressId, { widget: <CompletedAwaitingMediaWidget />, wide: true });
    const fallbackMessage = statusMessage && statusMessage.trim() ? statusMessage : t('directorConsole.ui.jobCompleteCheckGallery');
    await dirSay(fallbackMessage, null, false, 200);
    setStatus("done");
  }, [backendBase, conversationId, dirSay, handleRestart, routePrefix, updateMsg, userId]);

  const consumeDraft = useCallback(() => {
    setResumeDraft(null);
    setShouldPersistDraft(false);
    persistDraft(null);
  }, [persistDraft]);

  const launchPlannedJob = useCallback(async () => {
    if (!pendingLaunchPlan || jobRunningRef.current || launchInProgress) return;
    jobRunningRef.current = true;
    setLaunchInProgress(true);
    setStatus("processing");
    setPhase("chat");
    setCurrentStep("chat");
    userSay(t('directorConsole.ui.launchStartJob'));

    const progressId = uid();
    setMsgs((current) => [...current, {
      id: progressId,
      from: "director",
      widget: <ProgressWidget label={t('directorConsole.ui.jobLaunchedTracking')} progress={24} detail={t('directorConsole.ui.processingStep', { step: 1, total: 4 })} />,
      wide: true
    }]);
    scrollToBottom();

    try {
      const start = await sendChatMessage("Launch job", {
        baseUrl: backendBase,
        routePrefix,
        conversationId: pendingLaunchPlan.conversationId,
        userId: pendingLaunchPlan.sessionId,
        authToken: pendingLaunchPlan.authToken,
        viralBoost,
        launchPlanId: pendingLaunchPlan.plan.plan_id,
      });

      const nextConversationId = typeof start.conversation_id === "string" && start.conversation_id.trim()
        ? start.conversation_id.trim()
        : pendingLaunchPlan.conversationId;
      persistConversationId(nextConversationId ?? null);

      const launchId = start.status === "running" ? start.workflow_run_id
        : (start.job_ids?.[0] ?? extractPrimaryJobId(start));
      setLaunchStateText(`Job launched — tracking status… ${launchId ? `ID: ${launchId}` : ""}`.trim());

      if (start.status === "running") {
        // Persist BEFORE polling: if the tab reloads mid-render, the next load
        // reconnects to this run instead of stranding the user on the greeting.
        saveActiveRun(start.workflow_run_id, subType?.id);
        const polled = await pollRun(start.workflow_run_id, pendingLaunchPlan.authToken, progressId);
        await finalizeRun(polled, progressId, {
          conversationId: nextConversationId ?? null,
          jobId: start.job_ids?.[0] || start.workflow_run_id,
          authToken: pendingLaunchPlan.authToken,
          sessionId: pendingLaunchPlan.sessionId
        });
      } else {
        await finalizeRun(start, progressId, {
          conversationId: nextConversationId ?? null,
          jobId: start.job_ids?.[0] ?? null,
          authToken: pendingLaunchPlan.authToken,
          sessionId: pendingLaunchPlan.sessionId
        });
      }
      consumeDraft();
      setPendingLaunchPlan(null);
      setLaunchStateText(null);
      refreshUserCredits();
    } catch (error) {
      clearActiveRun();
      setStatus("error");
      const rawMsg = error instanceof Error ? error.message : t('directorConsole.ui.somethingWentWrong');
      const message = rawMsg === "Failed to fetch" || rawMsg.includes("service_unavailable") || rawMsg.includes("tool_dispatch_timeout") || rawMsg.includes("operation_timeout")
        ? t('directorConsole.ui.serverSlow') : rawMsg;
      updateMsg(progressId, { widget: <FailedWidget message={message} onRetry={() => void handleRestart()} />, wide: true });
      setLaunchStateText(t('directorConsole.ui.launchFailed', { message }));
      await dirSay(t('directorConsole.ui.errorRestartAdjust', { message }), null, false, 0);
    } finally {
      setLaunchInProgress(false);
      jobRunningRef.current = false;
    }
  }, [backendBase, consumeDraft, dirSay, finalizeRun, handleRestart, launchInProgress, pendingLaunchPlan, persistConversationId, pollRun, routePrefix, scrollToBottom, subType, updateMsg, userSay, viralBoost]);

  async function handleLaunch(
    selectedOutputType: OutputType,
    selectedSubType: SubType,
    currentAssets: Record<string, string | File>,
    currentDescription: string,
    selectedVideoDuration: VideoDurationChoice | null,
    selectedAspectRatio: AspectRatioChoice | null,
  ) {
    if (jobRunningRef.current || launchInProgress) return;
    if (getMissingRequiredAssets(selectedSubType, currentAssets).length > 0) {
      setStatus("ready");
      setCurrentStep("asset");
      setCurrentAssetKey(getMissingRequiredAssets(selectedSubType, currentAssets)[0] ?? null);
      setCurrentAssetOptional(false);
      await dirSay(
        t('directorConsole.ui.needImage'),
        <AssetInput
          assetKey={getMissingRequiredAssets(selectedSubType, currentAssets)[0] ?? selectedSubType.assets[0]}
          onSubmit={(payload) => handleAsset(
            selectedOutputType,
            selectedSubType,
            selectedSubType.assets.map((assetKey) => ({ key: assetKey, optional: false })),
            0,
            (getMissingRequiredAssets(selectedSubType, currentAssets)[0] ?? selectedSubType.assets[0]) as AssetKey,
            payload,
            currentAssets,
          )}
        />,
        true,
        120,
      );
      return;
    }
    jobRunningRef.current = true;
    clearWidgets();
    userSay(t('directorConsole.ui.buildFinalSummary'));
    setStatus("processing");
    setPhase("chat");
    setCurrentStep("chat");
    setCurrentAssetKey(null);
    setCurrentAssetOptional(false);
    setPendingLaunchPlan(null);
    setLaunchStateText(null);

    const progressId = uid();
    setMsgs((current) => [...current, {
      id: progressId,
      from: "director",
      widget: <ProgressWidget label={t('directorConsole.conversation.startingUp')} progress={2} detail={t('directorConsole.ui.processingStep', { step: 1, total: 4 })} />,
      wide: true
    }]);
    scrollToBottom();

    try {
      const authToken =
        typeof window !== "undefined" ? window.localStorage.getItem("auth_token") || undefined
          : undefined;
      const resolvedAssets: Record<string, string> = {};

      for (const [key, value] of Object.entries(currentAssets)) {
        if (value instanceof File || (typeof value === "string" && value.startsWith("blob:"))) {
          if (!authToken) {
            const label = ASSET_DEFS[key as AssetKey] ? assetLabel(t, key as AssetKey) : key;
            await dirSay(t('directorConsole.conversation.signInFirst', { asset: label }), null, false, 0);
            setStatus("ready");
            return;
          }
          updateMsg(progressId, {
            widget: <ProgressWidget label={t('directorConsole.ui.uploadingAsset', { label: ASSET_DEFS[key as AssetKey] ? assetLabel(t, key as AssetKey) : key })} progress={8} detail={t('directorConsole.ui.processingStep', { step: 1, total: 4 })} />,
            wide: true
          });

          let file: File;
          if (value instanceof File) {
            file = value;
          } else {
            // Fallback for cases where we only have the blob URL (should be rare now)
            const response = await fetch(value);
            const blob = await response.blob();
            const ext = blob.type.includes("/") ? blob.type.split("/")[1] : "bin";
            file = new File([blob], `${key}.${ext}`, { type: blob.type || "application/octet-stream" });
          }

          resolvedAssets[key] = await uploadToWebStorage(file, { authToken, baseUrl: backendBase, type: "chat-media" });
        } else {
          resolvedAssets[key] = value as string;
        }
      }

      const assetLines = Object.entries(resolvedAssets)
        .filter(([, value]) => value)
        .map(([key, value]) => `${ASSET_DEFS[key as AssetKey]?.label ?? key}: ${value}`)
        .join("\n");

      const brief = [
        "[Director Brief]",
        `Output type: ${selectedOutputType}`,
        `Job type: ${selectedSubType.label}`,
        ...(selectedOutputType === "video" && selectedVideoDuration ? [`Duration preference: ${selectedVideoDuration}s`] : []),
        ...(selectedOutputType === "video" && selectedAspectRatio ? [`Aspect ratio: ${selectedAspectRatio}`] : []),
        assetLines,
        "",
        // For presenter/spokesperson jobs, add a script: line so the backend planner can extract the voice text
        ...((["presenter", "product"].includes(selectedSubType.id) && currentDescription)
          ? [`script: ${currentDescription}`]
          : []),
        "User's vision:",
        currentDescription,
      ].filter(Boolean).join("\n");

      updateMsg(progressId, {
        widget: <ProgressWidget label={t('directorConsole.ui.submittingToDirector')} progress={12} detail={t('directorConsole.ui.processingStep', { step: 1, total: 4 })} />,
        wide: true
      });
      const launchConversationId: string | null = null;

      const start = await sendChatMessage(brief, {
        baseUrl: backendBase,
        routePrefix,
        conversationId: launchConversationId,
        userId: userId || getStoredUserId(),
        fileUrls: Object.values(resolvedAssets).filter((value) => value && typeof value === "string" && !value.startsWith("blob:")),
        authToken,
        viralBoost,
      });

      const nextConversationId = typeof start.conversation_id === "string" && start.conversation_id.trim() ? start.conversation_id.trim() : conversationId;
      persistConversationId(nextConversationId ?? null);

      const plannedState = extractPlannedState(start.raw);
      if (start.status === "succeeded" && plannedState.planned && plannedState.toolCount > 0) {
        const directorPlan = extractDirectorPlan(start.raw);
        updateMsg(progressId, { widget: null });
        if (!directorPlan) {
          await dirSay(t('directorConsole.ui.couldNotBuildSummary'), null, false, 120);
          setStatus("ready");
          return;
        }
        setPendingLaunchPlan({
          plan: directorPlan,
          conversationId: nextConversationId ?? null,
          authToken,
          sessionId: userId || getStoredUserId()
        });
        setLaunchStateText(null);
        await dirSay(t('directorConsole.ui.finalSummaryReady'), null, false, 120);
        setStatus("ready");
        return;
      }

      if (start.status === "running") {
        consumeDraft();
        if (start.message && start.message.trim()) {
          await dirSay(start.message, null, false, 120);
        }
        updateMsg(progressId, {
          widget: <ProgressWidget label={t('directorConsole.ui.directorWorking')} progress={25} detail={t('directorConsole.ui.processingStep', { step: 2, total: 4 })} />,
          wide: true
        });
        // Persist BEFORE polling so a mid-render reload reconnects to this run.
        saveActiveRun(start.workflow_run_id, selectedSubType.id);
        const polled = await pollRun(start.workflow_run_id, authToken, progressId);
        await finalizeRun(polled, progressId, {
          conversationId: nextConversationId ?? null,
          jobId: start.job_ids?.[0] ?? null,
          authToken,
          sessionId: userId || getStoredUserId()
        });
        return;
      }

      consumeDraft();
      await finalizeRun(start, progressId, {
        conversationId: nextConversationId ?? null,
        jobId: start.job_ids?.[0] ?? null,
        authToken,
        sessionId: userId || getStoredUserId()
      });
    } catch (error) {
      clearActiveRun();
      setStatus("error");
      const rawMsg = error instanceof Error ? error.message : t('directorConsole.ui.somethingWentWrong');
      // "Failed to fetch" typically means the server is cold-starting (Render free tier) or the network dropped
      const message = rawMsg === "Failed to fetch" || rawMsg.includes("service_unavailable") || rawMsg.includes("tool_dispatch_timeout") || rawMsg.includes("operation_timeout")
        ? t('directorConsole.ui.serverSlow') : rawMsg;
      const retryLaunch = () => {
        jobRunningRef.current = false;
        void handleLaunch(
          selectedOutputType,
          selectedSubType,
          currentAssets,
          currentDescription,
          selectedVideoDuration,
          selectedAspectRatio,
        );
      };
      updateMsg(progressId, { widget: <FailedWidget message={message} onRetry={retryLaunch} />, wide: true });
      await dirSay(t('directorConsole.ui.errorTryAgain', { message }), null, false, 0);
    } finally {
      jobRunningRef.current = false;
    }
  }

  useEffect(() => {
    void getDirectorCatalog({ baseUrl: backendBase, routePrefix }).catch(() => undefined);
    // Wake-up ping: hit /health so the backend (Render free tier) is warm
    // by the time the user submits a launch. Fire-and-forget; ignore failures.
    if (typeof window !== "undefined") {
      const healthUrl = `${(backendBase || "").replace(/\/$/, "")}/health`;
      void fetch(healthUrl, { method: "GET", cache: "no-store" }).catch(() => undefined);
    }
  }, [backendBase, routePrefix]);

  useEffect(() => {
    pollCancelledRef.current = false;
    return () => {
      pollCancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      setBootstrapped(true);
      return;
    }

    const storedConversationId = window.localStorage.getItem(CONVERSATION_STORAGE_KEY);
    if (storedConversationId) setConversationId(storedConversationId);
    setUserId(getStoredUserId());

    const storedDraft = readStoredDirectorDraft(subTypes);
    if (!storedDraft) {
      setBootstrapped(true);
      return;
    }

    const restored = findSubTypeById(storedDraft.subTypeId, subTypes);
    if (!restored) {
      persistDraft(null);
      setBootstrapped(true);
      return;
    }

    setOutputType(restored.outputType);
    setSubType(restored.subType);
    setAssets(storedDraft.assets);
    setAssetDisplayLabels(
      Object.fromEntries(
        Object.entries(storedDraft.assets).map(([key, value]) => [
          key,
          fallbackAssetSelectionLabel(t, key as AssetKey, typeof value === "string" ? value : value.name),
        ]),
      ),
    );
    setDescription(storedDraft.description);
    setVideoDuration(storedDraft.videoDuration || null);
    setAspectRatio(storedDraft.aspectRatio || null);
    setResumeDraft(storedDraft);
    const missingRequiredAssets = getMissingRequiredAssets(restored.subType, storedDraft.assets);
    setPhase("chat");
    setStatus("ready");
    setCurrentStep(missingRequiredAssets.length > 0 ? "asset" : "confirm");
    setCurrentAssetKey(missingRequiredAssets[0] ?? null);
    setCurrentAssetOptional(false);
    setBootstrapped(true);
  }, [persistDraft, subTypes]);

  useEffect(() => {
    if (!shouldPersistDraft) {
      persistDraft(null);
      return;
    }
    if (!outputType || !subType || !description.trim()) {
      persistDraft(null);
      return;
    }
    persistDraft({
      outputType,
      subTypeId: subType.id,
      assets,
      description: description.trim(),
      videoDuration,
      aspectRatio,
      savedAt: new Date().toISOString(),
    });
  }, [aspectRatio, assets, description, outputType, persistDraft, shouldPersistDraft, subType, videoDuration]);

  // The opening greeting + workflow picker, in the CURRENT language. Extracted
  // so the language toggle can replay it live (see the i18n effect below).
  const runIntro = useCallback(async () => {
    showTool(tool); // the chat is now this tool's; the board follows it
    setStatus("screening");
    setCurrentStep("output");
    // COMPASS-DRIVEN (2026-07-22): the tool is already chosen at the top of the
    // page, so the console drops the in-chat picker and goes STRAIGHT into that
    // tool's flow. No maximize on load — the landing pitch stays visible; the ⛶
    // button (or a running job) takes it fullscreen when the user wants.
    if (tool === "story" && !storyAvailableRef.current) {
      setStatus("idle");
      storySoonSaidRef.current = true;
      // Dropped if the answer turned yes while it was being typed: the re-run then says the real intro alone.
      await dirSay(t('directorConsole.ui.storyComingSoon'), null, false, 200, () => storyAvailableRef.current === true);
      return;
    }
    storySoonSaidRef.current = false;
    if (tool === "corrido" || tool === "story" || tool === "ads") {
      const subId = tool === "corrido" ? "corrido" : tool === "story" ? "tiktok_story" : "ads";
      const sub = getTranslatedSubTypes(t).video.find((s) => s.id === subId);
      if (sub) {
        setSubType(sub);
        setStatus("ready");
        await dirSay(
          tool === "corrido" ? t('directorConsole.workflow.corridoIntro')
            : tool === "story" ? t('directorConsole.workflow.storyIntro')
            : t('directorConsole.workflow.adsIntro'),
          tool === "corrido" ? <CorridoChatFlow /> : tool === "story" ? <StoryChatFlow /> : <AdsChatFlow />,
          true, 300,
        );
        return;
      }
    }
    if (tool === "rap") {
      setStatus("idle");
      await dirSay(t('directorConsole.ui.rapComingSoon'), null, false, 200);
      return;
    }
    // Fallback (console used without a compass): the classic picker.
    await dirSay(t('landing.console.welcome'), null, false, 300);
    await dirSay(
      t('landing.console.chooseWorkflow'),
      <WorkflowPicker
        onCorrido={() => {
          const sub = getTranslatedSubTypes(t).video.find((s) => s.id === "corrido");
          if (sub) void handleSubTypeRef.current("video", sub);
        }}
        onStory={() => {
          const sub = getTranslatedSubTypes(t).video.find((s) => s.id === "tiktok_story");
          if (sub) void handleSubTypeRef.current("video", sub);
        }}
        onAds={() => {
          const sub = getTranslatedSubTypes(t).video.find((s) => s.id === "ads");
          if (sub) void handleSubTypeRef.current("video", sub);
        }}
        onOther={() => {
          void dirSay(t('landing.console.whatMaking'), <OutputPicker onSelect={(type) => void handleOutputTypeRef.current(type)} />, true, 450);
        }}
      />,
      true,
      500,
    );
  }, [dirSay, showTool, t, tool]);
  runIntroRef.current = runIntro;

  // COMPASS SWITCH: when the tool up top changes, reset the console and drop
  // into the new tool's flow.
  // Not while a render is running, though: a console job, or a Corrido/Story
  // widget that is busy on the studio bus (widgetBusyRef). Clearing the chat
  // unmounts the widget, and the story flow keeps no reconnect record, so a
  // paid render would drop off the screen mid-flight. The switch is held
  // instead: chat and board stay on the busy tool, and the compass pick is
  // applied on the next compass change (compared with the tool on screen, so
  // swiping back to the busy tool is a no-op), when the console comes back on
  // screen from the welcome or storybook stop (the tool prop doesn't change
  // on that trip: corrido busy -> story held -> welcome -> Story card), or by
  // the restart button. It is deliberately NOT applied the moment the job
  // finishes, because that moment is when the storyboard, clip review or
  // finished video lands, and wiping it then would lose the same work one
  // step later. (2026-09-24)
  const prevToolRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!bootstrapped) return;
    if (prevToolRef.current === undefined) { prevToolRef.current = tool; showTool(tool); return; } // first load handled by the intro effect
    prevToolRef.current = tool;
    if (!visible) return;
    if (shownToolRef.current === tool) return;
    if (jobRunningRef.current || widgetBusyRef.current) return;
    setMaximized(false);
    setMsgs([]);
    resetDraft();
    introQueuedRef.current = true;
    void runIntroRef.current();
  }, [tool, visible, bootstrapped, resetDraft, showTool]);

  // The story studio's answer lands AFTER the intro on a fresh /?tool=story (the
  // config request is still in flight when the console mounts), so the intro
  // said "coming soon" from the default. When the answer is yes, and the chat
  // still shows only that line, the intro runs again (review, 2026-10-03).
  useEffect(() => {
    if (!storyAvailable || !storySoonSaidRef.current || tool !== "story" || !visible || !bootstrapped) return;
    if (jobRunningRef.current || widgetBusyRef.current) return;
    storySoonSaidRef.current = false;
    setMsgs([]);
    resetDraft();
    introQueuedRef.current = true;
    void runIntroRef.current();
  }, [storyAvailable, tool, visible, bootstrapped, resetDraft]);

  // Hidden (welcome / storybook stop) means not fullscreen either. The
  // fullscreen console isn't inert, so Shift+Tab reaches the compass behind
  // it, and a stop picked from there hid the console with the page scroll
  // still locked.
  useEffect(() => {
    if (!visible) setMaximized(false);
  }, [visible]);

  // DELIBERATE resume from the gallery: /?resume_corrido=<run_id> opens the
  // corrido flow directly (the flow itself reads the run id and loads the
  // clips). Query-param entry = an explicit user click in ChopShop, never a
  // banner that can hijack an in-progress build.
  const resumeHandledRef = useRef(false);
  useEffect(() => {
    if (!bootstrapped || resumeHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get("resume_corrido")) return;
    resumeHandledRef.current = true;
    introQueuedRef.current = true; // skip the greeting; go straight to the tool
    const sub = getTranslatedSubTypes(t).video.find((s) => s.id === "corrido");
    if (sub) void handleSubTypeRef.current("video", sub);
  }, [bootstrapped, t]);

  // Reconnect to an in-flight render after a plain page refresh. The render
  // runs on the backend GPU and keeps going across a reload; this re-attaches
  // the progress bar and resumes polling so the user is never dumped back on a
  // reset greeting wondering whether their job is alive. (resume_corrido above
  // is a separate, deliberate gallery entry point and takes precedence.)
  const activeRunHandledRef = useRef(false);
  useEffect(() => {
    if (!bootstrapped || activeRunHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("resume_corrido")) return; // deliberate gallery resume owns this load
    const active = readActiveRun();
    if (!active) return;
    activeRunHandledRef.current = true;
    introQueuedRef.current = true; // we're mid-job — skip the greeting
    jobRunningRef.current = true;
    setPhase("chat");
    setStatus("processing");
    setCurrentStep("chat");
    const restored = active.subTypeId ? findSubTypeById(active.subTypeId, subTypes) : null;
    if (restored) {
      setOutputType(restored.outputType);
      setSubType(restored.subType);
    }
    const progressId = uid();
    setMsgs((current) => [...current, {
      id: progressId,
      from: "director",
      text: t('directorConsole.ui.reconnectedToRender'),
      widget: <ProgressWidget label={t('directorConsole.ui.reconnecting')} progress={30} detail={t('directorConsole.ui.stillProcessingShort')} />,
      wide: true,
    }]);
    scrollToBottom();
    const authToken = typeof window !== "undefined" ? window.localStorage.getItem("auth_token") || undefined : undefined;
    void (async () => {
      try {
        const polled = await pollRun(active.runId, authToken, progressId);
        await finalizeRun(polled, progressId, {
          conversationId,
          jobId: active.runId,
          authToken,
          sessionId: getStoredUserId(),
        });
      } catch (error) {
        clearActiveRun();
        const message = error instanceof Error ? error.message : t('directorConsole.ui.lostContact');
        updateMsg(progressId, { widget: <FailedWidget message={message} onRetry={() => void handleRestart()} />, wide: true });
        await dirSay(t('directorConsole.ui.reconnectFailed', { message }), null, false, 0);
        setStatus("error");
      } finally {
        jobRunningRef.current = false;
      }
    })();
  }, [bootstrapped, conversationId, dirSay, finalizeRun, handleRestart, pollRun, scrollToBottom, subTypes, updateMsg]);

  useEffect(() => {
    if (!bootstrapped || resumeDraft || msgs.length > 0) return;
    if (introQueuedRef.current) return;
    introQueuedRef.current = true;
    void runIntro();
  }, [bootstrapped, msgs.length, resumeDraft, runIntro]);

  // Language toggle: when the user flips EN<->ES and they're still at the
  // intro (no workflow started yet), replay the greeting in the new language
  // so the switch is visibly working — not just the static page chrome.
  const introLangRef = useRef(i18n.language);
  useEffect(() => {
    if (introLangRef.current === i18n.language) return;
    introLangRef.current = i18n.language;
    if (typeof document !== "undefined") document.documentElement.lang = i18n.language;
    // Only replay if the user hasn't left the intro (no subtype chosen).
    if (subType || outputType) return;
    setMsgs([]);
    introQueuedRef.current = false;
  }, [i18n.language, subType, outputType]);

  useEffect(() => {
    if (!pendingLaunchPlan) return;
    requestAnimationFrame(() => {
      scrollToBottom();
    });
  }, [pendingLaunchPlan, scrollToBottom]);

  // While maximized, lock the page scroll so there's no second scrollbar and no
  // landing-page scroll behind the full-screen console. The html class hides
  // the site navbar (index.css): the hero's z-10 container is a stacking
  // context, so the console's z-index 200 can't lift it above the fixed z-50
  // navbar, which covered the console's header (Home, language, status).
  // Portaling the console out would remount it and lose the chat.
  const fullscreen = maximized && visible;
  useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.classList.add("console-maximized");
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.classList.remove("console-maximized");
    };
  }, [fullscreen]);

  return (
    <div
      id="director-console" data-testid="landing-director-console" style={{
        fontFamily: "system-ui, sans-serif",
        background: P.console,
        borderRadius: maximized ? 0 : 20,
        border: maximized ? "none" : `1px solid ${P.consoleEdge}`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        // /#director-console lands with the header clear of the fixed navbar
        // (69px): 5rem instead of the anchors' usual 6rem (scroll-mt-24),
        // because this card is a whole screen tall and every px of offset
        // pushes the chat input further under the fold. (2026-09-24)
        scrollMarginTop: "5rem",
        // Normal: a capped card on the landing page. Maximized: take over the
        // whole viewport (fixed, inset 0) so the workspace is the only thing on
        // screen and the page can't scroll behind it.
        // In the page, the height comes from .ldc-root in the console
        // stylesheet (svh with a vh fallback — two values an inline style
        // cannot carry; see IN-PAGE HEIGHT there). Maximized, 100dvh follows
        // the mobile browser chrome as it collapses, which is right for a
        // fixed overlay.
        ...(maximized
          ? { position: "fixed" as const, inset: 0, height: "100dvh", width: "100%", zIndex: 200 }
          : { position: "relative" as const }),
        // Lift it off the page: a real drop shadow plus the brand glow, so the
        // panel edge is readable even where the two darks are close.
        boxShadow: maximized ? "none" : `0 24px 70px rgba(0,0,0,.65), 0 0 60px rgba(56,189,248,.06), 0 0 120px rgba(56,189,248,.04)`,
      }}
      className={maximized ? "ldc-maximized" : "ldc-root"}
    >
      {bgMediaUrl ? (
        /\.(mp4|webm|mov)$/i.test(bgMediaUrl)
          ? <video src={bgMediaUrl} autoPlay muted loop playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.22, pointerEvents: "none" }} />
          : <img src={bgMediaUrl} alt={t('directorConsole.ui.directorBackground')} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.2, pointerEvents: "none" }} />
      ) : null}
      <div className="ldc-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", minHeight: 56, flexShrink: 0, background: "rgba(5,7,11,.92)", borderBottom: `1px solid ${P.border}`, backdropFilter: "blur(16px)", position: "relative", zIndex: 10, gap: 12, flexWrap: "wrap" }}>
        <div className="ldc-brand" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <DAvatar />
          <div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 6, lineHeight: 1, background: `linear-gradient(90deg,${P.cyan},${P.pink})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t('directorConsole.ui.directorWordmark')}
            </div>
            <div style={{ fontSize: 11, color: P.dim, letterSpacing: 3, lineHeight: 1.4, marginTop: 1 }}>{t('directorConsole.ui.brandMark')}</div>
          </div>
        </div>
        <div className="ldc-header-actions" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {/* HOME — exit the full-screen workspace back to the landing page. */}
          {maximized && (
            <button
              type="button" data-testid="landing-director-home-button"
              onClick={() => { setMaximized(false); requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "auto" })); }}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(56,189,248,.12)", border: `1px solid ${P.pink}`, color: P.offwhite, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, letterSpacing: 0.5, cursor: "pointer" }}
              title={t('directorConsole.ui.backToLanding')} aria-label={t('directorConsole.ui.home')}
            >
              <span aria-hidden="true">⌂</span> <span className="ldc-btn-label">{t('directorConsole.ui.home')}</span>
            </button>
          )}
          {/* EXPAND — go fullscreen on demand (we no longer auto-maximize on
              load, so the landing pitch + compass stay visible until asked). */}
          {!maximized && (
            <button
              type="button" data-testid="landing-director-expand-button"
              onClick={() => setMaximized(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${P.border}`, color: P.muted, borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              title={t('directorConsole.ui.expandFullscreen')} aria-label={t('directorConsole.ui.expandFullscreen')}
            >
              <span aria-hidden="true">⛶</span> <span className="ldc-btn-label">{t('directorConsole.ui.expand')}</span>
            </button>
          )}
          <LangToggle />
          {/* BOARD toggle removed — the storyboard is not an optional panel.
              It is a main component of every tool here, so it is always on
              screen to the left of this chat. */}
          {isAuthenticated && (
            <div
              data-testid="landing-director-credits" title={t('directorConsole.ui.creditBalanceTitle')} style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                border: `1px solid ${P.borderHi}`,
                background: "rgba(227,179,65,.08)",
                borderRadius: 999,
                padding: "5px 10px",
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 13 }}></span>
              <span style={{ fontSize: 13, fontWeight: 800, color: P.gold, fontFamily: "'Rajdhani', sans-serif", letterSpacing: 0.5 }}>
                {creditBalance === null ? "—" : creditBalance.toLocaleString()}
              </span>
            </div>
          )}
          {/* Buy Credits removed — no pricing system yet. */}
          <StatusDot status={status} />
          {/* The glyph (2026-09-24): on a phone .ldc-btn-label hides the "BG"
              word, and this was left an empty, nameless box. */}
          <button
            type="button" onClick={() => bgUploadInputRef.current?.click()}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${P.border}`, color: P.muted, borderRadius: 7, padding: "6px 8px", fontSize: 12, cursor: "pointer" }}
            title={t('directorConsole.ui.setBackgroundMedia')} aria-label={t('directorConsole.ui.setBackgroundMedia')}
          ><ImageIcon aria-hidden="true" style={{ width: 14, height: 14 }} /><span className="ldc-btn-label">{t('directorConsole.ui.bgShort')}</span>
          </button>
          <button type="button" data-testid="landing-director-restart-button" onClick={() => void handleRestart()} style={{ background: "transparent", border: `1px solid ${P.border}`, color: P.muted, width: 30, height: 30, borderRadius: 7, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }} title={t('directorConsole.ui.startOver')} aria-label={t('directorConsole.ui.startOver')}>
            <span aria-hidden="true">↺</span>
          </button>
        </div>
        <input
          ref={bgUploadInputRef}
          type="file" data-consent-tool="corrido" accept="image/*,video/*" style={{ display: "none" }}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            const objectUrl = URL.createObjectURL(file);
            setBgMediaUrl(objectUrl);
          }}
        />
      </div>

      {phase === "screening" && (
        <div className="ldc-screening" style={{ background: `linear-gradient(90deg,${P.cyanDim},${P.pinkDim},transparent)`, borderBottom: `1px solid ${P.border}`, padding: "5px 16px", fontSize: 11, letterSpacing: 2.5, color: P.cyan, display: "flex", alignItems: "center", gap: 7, flexShrink: 0, zIndex: 9, fontFamily: "'Rajdhani', sans-serif" }}>
          <span></span> {t('directorConsole.ui.screeningBanner')}
        </div>
      )}

      {/* Redundant English promo strip removed — the greeting cards already
          offer Corrido + TikTok, in the selected language. */}

      {/* THE WORKSPACE — storyboard canvas beside the chat. The canvas is where
          every job gets approved, so it is always on screen; the chat is how you
          talk to the Director about what's on it. */}
      <div className="ldc-workspace" ref={workspaceRef}>
        <div
          data-testid="landing-director-board"
          className="ldc-canvas"
          /* the ::before section label is CSS `content`, which t() cannot
             reach — feed it through a custom property instead */
          style={{ ["--ldc-board-label" as string]: JSON.stringify(t('directorConsole.ui.boardLabel')) }}
        >
          {/* The board mirrors ONLY the active tool's work: the compass tool,
              or the busy one while a compass switch is held (shownTool). */}
          <StoryboardCanvas hint={t('directorConsole.workflow.boardHint')} activeService={shownTool === "rap" ? null : shownTool === "story" ? "story" : shownTool === "ads" ? "ads" : "corrido"} />
        </div>

        <div className="ldc-chat" style={{ width: chatWidth }}>
          {/* Drag the seam to trade space between board and chat — CapCut lets
              you do this and which one you want bigger depends on the job. */}
          <div
            className="ldc-resize" data-testid="landing-director-chat-resize" data-dragging={resizing ? "true" : "false"}
            onPointerDown={onResizeStart}
            role="separator" aria-orientation="vertical" aria-label={t('directorConsole.ui.resizeChatPanel')} />
      <div className="ldc-chat-label">{t('directorConsole.ui.chatLabel')}</div>
      <div ref={chatScrollRef} className="ldc-chat-log" data-testid="landing-director-chat-log" style={{ flex: 1, overflowY: "auto", padding: "8px 12px 8px", display: "flex", flexDirection: "column", gap: 10, position: "relative", zIndex: 1, background: `radial-gradient(ellipse at 20% 80%, rgba(147,124,240,.05) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(56,189,248,.04) 0%, transparent 60%)` }}>
        {resumeDraft && outputType && subType && description && msgs.length === 0 && (
          <Bubble
            msg={{
              id: uid(),
              from: "director",
              text: getMissingRequiredAssets(subType, assets).length > 0
                ? t('directorConsole.ui.chooseFaceImage') : isAuthenticated
                  ? t('directorConsole.ui.draftRestored') : t('directorConsole.ui.draftRestoredSignedOut'),
              widget: getMissingRequiredAssets(subType, assets).length > 0 ? (
                <AssetInput
                  assetKey={getMissingRequiredAssets(subType, assets)[0] as AssetKey}
                  onSubmit={(payload) => handleAsset(
                    outputType,
                    subType,
                    subType.assets.map((assetKey) => ({ key: assetKey, optional: false })),
                    0,
                    getMissingRequiredAssets(subType, assets)[0] as AssetKey,
                    payload,
                    assets,
                  )}
                />
              ) : (
                <ConfirmCard
                  outputType={outputType}
                  subType={subType}
                  assets={assets}
                  assetDisplayLabels={assetDisplayLabels}
                  description={description}
                  isAuthenticated={isAuthenticated}
                  videoDuration={videoDuration}
                  aspectRatio={aspectRatio}
                  onDurationChange={setVideoDuration}
                  onAspectRatioChange={setAspectRatio}
                  onLaunch={(selectedDuration, selectedRatio) => {
                    void handleLaunch(outputType, subType, assets, description, selectedDuration, selectedRatio);
                  }}
                  onAdjust={() => {
                    void handleRestart();
                  }}
                />
              ),
              wide: true,
            }}
          />
        )}
        {msgs.map((msg) => (
          <Bubble key={msg.id} msg={msg} />
        ))}
        {typing && (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
            <DAvatar className="ldc-bubble-avatar" />
            <div style={{ background: P.raised, border: `1px solid ${P.border}`, borderRadius: 14, borderBottomLeftRadius: 3, padding: "10px 14px" }}>
              <Dots />
            </div>
          </div>
        )}
      </div>
      {pendingLaunchPlan ? (
        <div style={{ padding: "10px 12px 0", background: "rgba(5,7,11,.95)", borderTop: `1px solid ${P.border}`, flexShrink: 0 }}>
          <FinalSummaryCard
            plan={pendingLaunchPlan.plan}
            canLaunch={isAuthenticated}
            isLaunching={launchInProgress}
            launchStateText={launchStateText}
            onLaunch={() => {
              void launchPlannedJob();
            }}
          />
        </div>
      ) : null}
      <div
        style={{
          padding: "8px 12px 0",
          background: "rgba(5,7,11,.95)",
          borderTop: `1px solid ${P.border}`,
          flexShrink: 0,
        }}
      >
        {/* Viral Algorithm Boost panel removed — feature retired. */}
      </div>
      <form
        className="ldc-chat-form"
        onSubmit={(event) => {
          event.preventDefault();
          void handleChatSend();
        }}
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          padding: "10px 12px 12px",
          borderTop: `1px solid ${P.border}`,
          background: "rgba(5,7,11,.95)",
          flexShrink: 0
        }}
      >
        <input
          className="ldc-chat-input"
          id="landing-director-chat-input" data-testid="landing-director-chat-input" name="director_chat_input" value={chatInput}
          onChange={(event) => setChatInput(event.target.value)}
          placeholder={t('landing.console.inputPlaceholder')}
          aria-label={t('landing.console.inputAriaLabel')}
          style={{
            flex: 1,
            background: P.surface,
            border: `1px solid ${P.borderHi}`,
            borderRadius: 10,
            color: P.offwhite,
            padding: "10px 12px",
            fontSize: 13,
            outline: "none" }}
        />
        <button
          className="ldc-chat-send"
          type="submit" data-testid="landing-director-chat-send" disabled={sendingChat || chatInput.trim().length === 0}
          style={{
            background: sendingChat || chatInput.trim().length === 0
              ? "rgba(255,255,255,.08)" : `linear-gradient(135deg,${P.pink},${P.purple})`,
            color: sendingChat || chatInput.trim().length === 0 ? P.muted : P.white,
            border: "none",
            borderRadius: 10,
            padding: "10px 16px",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 1,
            cursor: sendingChat || chatInput.trim().length === 0 ? "default" : "pointer",
            minWidth: 80
          }}
        >{t('directorConsole.ui.send')}
        </button>
      </form>
      {/* Footer Buy Credits bar removed — no pricing system yet. */}
        </div>{/* .ldc-chat */}
      </div>{/* .ldc-workspace */}
    </div>
  );
}
